import { useState, useEffect, useCallback, useMemo, useRef, cloneElement, Fragment } from "react";
import "./App.css";
import LoginPage        from "./LoginPage";
import SignPage         from "./SignPage";
import CountersignPage  from "./CountersignPage";
import HomePage         from "./HomePage";
import TestPanel        from "./TestPanel";
import ChangelogPage    from "./ChangelogPage";
import PricingRowsEditor from "./PricingRowsEditor";
import NewTemplateModal  from "./NewTemplateModal";
import DraftFromNotes    from "./DraftFromNotes";
import { PRESETS as PAYMENT_PRESETS, paymentPlanBullets } from "./shared/payment";
import { sectionListFor, templateTableLocator, subFromLocator } from "./shared/sections";
import { agreementFileName } from "./shared/filename";
import { shortDate, longDate } from "./shared/dates";
import { SOFTWARE_OPTIONS as HS_SOFTWARE_OPTIONS } from "./shared/hubspotProducts";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayFormatted() {
  return new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
// The long form the agreement body reads in. Delegates to shared/dates so the
// three ways this app writes a date can't drift; unlike the old version it also
// survives being handed a date that has already been formatted.
function formatDisplayDate(iso) {
  return longDate(iso);
}
function fill(text, data) {
  if (!text) return "";
  return text.replace(/\{\{(\w+)\}\}/g, (_, key) => data[key] !== undefined ? data[key] : `{{${key}}}`);
}
function truncate(str, len = 80) {
  if (!str) return "";
  return str.length > len ? str.slice(0, len) + "…" : str;
}

// ─── Section filter categories (Luke) ────────────────────────────────────────

const SECTION_FILTER_OPTIONS = [
  "Software & Services",
  "Pricing",
  "Licensing",
  "Annual Maintenance",
  "Hosting",
  "Service Level Agreement",
  "Restrictions / Limitations",
  "Assumptions / Client Responsibilities",
  "Normal Rates and Fees",
  "Payment Terms",
  "Other",
];

const SECTION_TYPE_LABELS = {
  heading: "Heading",
  paragraph: "Paragraph",
  paragraph_noindent: "Paragraph (no indent)",
  bullet: "Bullet",
  diamond_bullet: "Diamond Bullet",
  arrow_bullet: "Arrow Bullet",
  square_bullet: "Square Bullet",
  note: "Note",
  hr: "Horizontal Rule",
  spacer: "Spacer",
  title: "Title",
  title_italic: "Title (Italic)",
  subtitle: "Subtitle",
  doc_header: "Doc Header",
  pricing_table: "Pricing Table",
  pricing_total: "Pricing Total",
  signature: "Signature Block",
  bank_of_hours: "Bank of Hours Card",
  data_table: "Data Table",
};

// My Snippets are reusable *prose* clauses you add yourself — special terms,
// custom paragraphs, extra notes — kept separate from the product Templates.
// Pricing tables and structural blocks are authored on products, not here, so
// the snippet type picker only offers text section types.
const SNIPPET_TYPE_LABELS = {
  paragraph: "Paragraph",
  paragraph_noindent: "Paragraph (no indent)",
  heading: "Heading",
  bullet: "Bullet",
  diamond_bullet: "Diamond Bullet",
  arrow_bullet: "Arrow Bullet",
  square_bullet: "Square Bullet",
  note: "Note",
};

// ─── Product category config ───────────────────────────────────────────────

const CATEGORY_ORDER = ["yours", "software", "services", "legal"];
const CATEGORY_LABELS = {
  yours:    "My Templates",
  software: "Core Software",
  services: "Services & Consulting",
  legal: "Legal & Compliance",
};
// ─── Structured measure rows (fields with type "measure_rows") ────────────────
// Stored in fields[key] as a JSON string: [{ name, type }]. Kept as a string so
// it rides through the existing flat fields map, draft save, and snapshot intact.
function parseMeasureRows(raw) {
  if (Array.isArray(raw)) return raw;
  try { const a = JSON.parse(raw || "[]"); return Array.isArray(a) ? a : []; }
  catch { return []; }
}

// What prints after the measure name in the agreement. The type dropdown is an
// internal price tier ("Quality ID — developed") and reads like a note-to-self in
// front of a client, so each option can carry client-facing wording and each row
// can override it outright — including to "" for the measure name on its own.
function measureDefaultWording(opt) {
  if (!opt) return "";
  return opt.agreementLabel !== undefined ? opt.agreementLabel : opt.label;
}
function measureWording(entry) {
  return entry.display !== undefined ? entry.display : measureDefaultWording(entry.opt);
}

// Repeatable editor: one line per measure — name + type dropdown (price comes
// from the type) — which composeSections turns into real pricing-table rows.
function MeasureRowsField({ field, value, onChange }) {
  const opts = field.options || [];
  const rows = parseMeasureRows(value);
  const commit = (next) => onChange(JSON.stringify(next));
  const setRow = (i, patch) => commit(rows.map((r, j) => j === i ? { ...r, ...patch } : r));
  // Picking a different type re-points the wording at the new type's default —
  // unless the wording was typed by hand, which outranks any default.
  const setType = (i, r, type) => {
    const wasDefault = r.display === undefined
      || r.display === measureDefaultWording(opts.find(o => o.value === r.type));
    setRow(i, { type, display: wasDefault ? undefined : r.display });
  };
  const money = (n) => `$${Number(n || 0).toLocaleString("en-US")}`;
  const total = rows.reduce((sum, r) => sum + (opts.find(o => o.value === r.type)?.price || 0), 0);
  return (
    <div>
      {rows.length === 0 && (
        <div style={{fontSize:"11px",color:"#999",marginBottom:6,lineHeight:1.5}}>
          No measures added yet — click <strong>+ {field.addLabel || "Add row"}</strong>, then type the
          measure name and pick its type.
        </div>
      )}
      {/* Name gets its own full-width line: side by side the box was ~120px wide,
          the example placeholder truncated, and it stopped reading as typable. */}
      {rows.map((r, i) => {
        const opt = opts.find(o => o.value === r.type);
        return (
          <div key={i} style={{border:"1px solid #e3e6ef",borderRadius:6,padding:"8px",marginBottom:6,background:"#fbfcff"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:3}}>
              <label style={{fontSize:"10px",fontWeight:700,color:"#8a90a6",letterSpacing:"0.03em"}}>
                MEASURE {i + 1} NAME
              </label>
              <button
                type="button" title="Remove this measure"
                onClick={() => commit(rows.filter((_, j) => j !== i))}
                style={{background:"none",border:"none",color:"#c0392b",cursor:"pointer",fontSize:"13px",lineHeight:1,padding:0}}
              >✕</button>
            </div>
            <input
              type="text" className="input" style={{width:"100%",boxSizing:"border-box",marginBottom:6}}
              placeholder={field.namePlaceholder || "Type the measure name…"}
              value={r.name || ""}
              onChange={e => setRow(i, { name: e.target.value })}
            />
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <select
                className="input" style={{flex:"1 1 0",minWidth:0}}
                value={r.type || ""}
                onChange={e => setType(i, r, e.target.value)}
              >
                <option value="">Select type…</option>
                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <span style={{flex:"0 0 auto",fontSize:"12px",fontWeight:600,color:opt?"#1a3a6e":"#bbb",whiteSpace:"nowrap"}}>
                {opt ? money(opt.price) : "—"}
              </span>
            </div>
            {/* The type above is the internal price tier; this is what the client
                reads. Clear it to print the measure name on its own. */}
            <label style={{display:"block",fontSize:"10px",fontWeight:700,color:"#8a90a6",letterSpacing:"0.03em",margin:"7px 0 3px"}}>
              WORDING IN THE AGREEMENT
            </label>
            <input
              type="text" className="input" style={{width:"100%",boxSizing:"border-box"}}
              placeholder="Leave blank for just the measure name"
              value={measureWording(r)}
              onChange={e => setRow(i, { display: e.target.value })}
            />
            <div style={{fontSize:"10px",color:"#999",marginTop:4,lineHeight:1.4}}>
              Prints as: ➢&nbsp;{[(r.name || "").trim() || `Measure ${i + 1}`,
                                  measureWording(r) ? `— ${measureWording(r)}` : ""].filter(Boolean).join(" ")}
            </div>
          </div>
        );
      })}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6}}>
        <button
          type="button"
          onClick={() => commit([...rows, { name: "", type: opts[0]?.value || "" }])}
          style={{background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:4,color:"#1a3a6e",cursor:"pointer",fontSize:"11px",fontWeight:600,padding:"4px 10px"}}
        >+ {field.addLabel || "Add row"}</button>
        {rows.length > 0 && (
          <span style={{fontSize:"11px",color:"#555"}}>Total: <strong>{money(total)}</strong></span>
        )}
      </div>
    </div>
  );
}

const PRODUCT_CATEGORY = {
  "connectehr":          "software",
  "fhir":                "software",
  "cqm-solution":        "software",
  "cqm-solution-upgrade":"software",
  "cehrt":               "software",
  "tie":                 "software",
  "tie_hie_integration": "software",
  "uds":                 "software",
  "support":             "services",
  "gap-analysis":        "services",
  "registry":            "services",
  "nda":                 "legal",
  "baa":                 "legal",
};

// Classify a heading into one of the canonical section categories so UDS
// inserts can be appended to the matching section in composedSections.
function classifySectionHeading(headingText) {
  const t = (headingText || "").toLowerCase();
  if (!t) return "Other";
  if (t.includes("service level") || t.includes("sla")) return "Service Level Agreement";
  if (t.includes("hosting")) return "Hosting";
  if (t.includes("software") || t.includes("services to be provided") || t.includes("services provided")) return "Software & Services";
  if (t.includes("pricing") || t.includes("quote") || t.includes("fee schedule")) return "Pricing";
  if (t.includes("licens")) return "Licensing";
  if (t.includes("maintenance")) return "Annual Maintenance";
  if (t.includes("restriction") || t.includes("limitation")) return "Restrictions / Limitations";
  if (t.includes("assumption") || t.includes("client responsibilit")) return "Assumptions / Client Responsibilities";
  if (t.includes("rate") || t.includes("hourly")) return "Normal Rates and Fees";
  if (t.includes("payment")) return "Payment Terms";
  return "Other";
}

// Pair each section with the category of the most-recent preceding heading,
// so the preview can route clicks to the matching sidebar filter.
function annotateSectionsWithCategory(sections) {
  let current = "Other";
  return sections.map(s => {
    if (s.type === "heading") current = classifySectionHeading(s.content);
    return { section: s, category: current };
  });
}

// ─── Preview renderer ─────────────────────────────────────────────────────────

const EDITABLE_PREVIEW_TYPES = new Set([
  "paragraph","paragraph_noindent",
  "bullet","diamond_bullet","arrow_bullet","square_bullet",
  "note","title","title_italic","subtitle","pricing_total",
]);

function sectionKey(section) {
  if (section._customIdx !== undefined) return `c:${section._customIdx}`;
  if (section._srcKey) return `t:${section._srcKey}`;
  return null;
}

// Removing a custom section renumbers every one after it, so anything anchored
// to a later one ("c:5") would point at the wrong section — or at nothing, and a
// lost anchor means the text falls to the bottom of the agreement. Renumber the
// anchors along with the array, and re-anchor whatever pointed at the section
// being removed to that section's own anchor, so it stays where it looks.
function dropCustomSection(list, i) {
  return list.filter((_, j) => j !== i).map(s => {
    const m = /^c:(\d+)$/.exec(s._after || "");
    if (!m) return s;
    const n = Number(m[1]);
    if (n === i) return { ...s, _after: list[i]._after };
    return n > i ? { ...s, _after: `c:${n - 1}` } : s;
  });
}

// Where a section sitting in the preview belongs inside a template's own
// `sections` array: immediately after the nearest section above it that came
// from that template. Hosting, UDS, dynamic and payment sections have no index
// in the template, so they're stepped over — the answer is still the right
// neighbourhood. Returns 0 when nothing above it belongs to the template.
function templateInsertIndex(composed, section, productId) {
  const key = sectionKey(section);
  const at = key ? composed.findIndex(s => sectionKey(s) === key) : -1;
  if (at < 0) return null;
  for (let i = at - 1; i >= 0; i--) {
    const s = composed[i];
    if (s._srcProductId === productId && !s._srcSubType && Number.isInteger(s._srcSectionIdx))
      return s._srcSectionIdx + 1;
  }
  return 0;
}

// Types offered at an insert point, in the order they're most often reached for.
// Labels show the actual bullet glyph so the choice matches what lands on the page.
const INSERT_TYPES = [
  { type: "diamond_bullet",     label: "❖ Bullet" },
  { type: "arrow_bullet",       label: "➢ Sub-bullet" },
  { type: "square_bullet",      label: "■ Sub-sub" },
  { type: "heading",            label: "Heading" },
  { type: "paragraph",          label: "Paragraph" },
  { type: "note",               label: "Note" },
  { type: "spacer",             label: "Space" },
];

// A hover-revealed "+" between sections. Collapsed it is a thin strip, so the
// document still reads like a document; opened it offers the section types.
function InsertPoint({ onInsert }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <div className="prev-insert" onClick={() => setOpen(true)} title="Insert a section here">
        <span className="prev-insert-rule" />
        <span className="prev-insert-plus">+</span>
        <span className="prev-insert-rule" />
      </div>
    );
  }
  return (
    <div className="prev-insert prev-insert--open">
      {INSERT_TYPES.map(t => (
        <button key={t.type} type="button" className="prev-insert-type"
          onMouseDown={(e) => { e.preventDefault(); setOpen(false); onInsert(t.type); }}>
          {t.label}
        </button>
      ))}
      <button type="button" className="prev-insert-close" onMouseDown={(e) => { e.preventDefault(); setOpen(false); }}>✕</button>
    </div>
  );
}

// "Edit table" opens the row editor for a previewed pricing table — for this
// agreement only, or for the template it came from. Shown on every table that
// has a home in a template — body, hosting insert, UDS insert, single- or
// dual-year — and hidden on custom one-off tables, which have nowhere to save
// back to.
function EditTableButton({ section, onEditTable }) {
  if (!onEditTable || !templateTableLocator(section)) return null;
  return (
    <button type="button" className="prev-edit-table-btn"
      title="Edit this pricing table"
      onClick={(e)=>{e.stopPropagation();onEditTable(section);}}
      style={{background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:4,color:"#1a3a6e",cursor:"pointer",fontSize:"10px",fontWeight:600,padding:"2px 8px",whiteSpace:"nowrap"}}>
      Edit table
    </button>
  );
}

function renderPreview(sections, data, onPriceChange, onHeadingClick, inlineEdit, onRemove, onEditTable, onInsertAfter) {
  const { editKey, editContent, onStart, onChange, onSave } = inlineEdit || {};
  const annotated = annotateSectionsWithCategory(sections);
  return annotated.map(({ section, category }, i) => {
    const key = sectionKey(section);
    // Template headings stay click-to-filter, but a heading you just inserted has
    // to be typable or there'd be no way to name it.
    const isInsertedHeading = section.type === "heading" && section._customIdx !== undefined;
    const isEditable = !!key && (EDITABLE_PREVIEW_TYPES.has(section.type) || isInsertedHeading) && !!onStart;
    const isEditing  = isEditable && editKey === key;
    const NON_REMOVABLE = new Set(["doc_header","signature","hr","spacer"]);
    const isRemovable = !!key && !!onRemove && !NON_REMOVABLE.has(section.type) && isEditing;
    const wrapRemovable = (el) => isRemovable
      ? (<div key={i} className="prev-removable-wrap">
          {el}
          <button type="button" className="prev-remove-btn"
            title="Remove this section"
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(section); }}>
            <IconX/>
          </button>
        </div>)
      : el;

    if (isEditing) {
      return wrapRemovable(
        <textarea key={i} className="prev-inline-edit-textarea"
          value={editContent}
          onChange={e => onChange(e.target.value)}
          onBlur={() => onSave(key, editContent, section)}
          onKeyDown={e => { if (e.key === "Escape") { e.preventDefault(); onSave(key, editContent, section); } }}
          autoFocus
        />
      );
    }

    const content = fill(section.content || "", data);
    const isHeading = section.type === "heading";
    const headingClickable = isHeading && !!onHeadingClick && !!category && category !== "Other";

    const wrapEditable = (el) => isEditable
      ? cloneElement(el, {
          onClick: (e) => { e.stopPropagation(); onStart(key, section.content || "", section, category); },
          className: ((el.props.className || "") + " prev-editable").trim(),
          title: "Click to edit",
        })
      : el;
    const wrapHeading = (el) => headingClickable
      ? cloneElement(el, {
          onClick: (e) => { e.stopPropagation(); onHeadingClick(category, null); },
          className: ((el.props.className || "") + " prev-clickable").trim(),
          title: `Filter snippets: ${category}`,
        })
      : el;

    const node = (() => { switch (section.type) {
      case "doc_header":
        return (
          <div key={i} className="prev-doc-header">
            <span>Dynamic Health IT, Inc.</span>
            <img src="/api/logo-d" alt="" className="prev-doc-header-logo" />
          </div>
        );
      case "title_italic": return wrapEditable(<div key={i} className="prev-title-italic">{content}</div>);
      case "title":        return wrapEditable(<h1  key={i} className="prev-title">{content}</h1>);
      case "subtitle":     return wrapEditable(<h2  key={i} className="prev-subtitle">{content}</h2>);
      case "heading":      return isInsertedHeading
                                    ? wrapEditable(<h3 key={i} className="prev-heading">{content}</h3>)
                                    : wrapHeading(<h3  key={i} className="prev-heading">{content}</h3>);
      case "paragraph":    return wrapEditable(<p   key={i} className="prev-paragraph prev-indent">{content}</p>);
      case "paragraph_noindent": return wrapEditable(<p key={i} className="prev-paragraph">{content}</p>);
      case "bullet":       return wrapEditable(<p   key={i} className="prev-bullet">&#x2022;&nbsp;&nbsp;{content}</p>);
      case "diamond_bullet":return wrapEditable(<p  key={i} className="prev-diamond">❖&nbsp;&nbsp;{content}</p>);
      case "arrow_bullet": return wrapEditable(<p   key={i} className="prev-arrow">➢&nbsp;&nbsp;{content}</p>);
      case "square_bullet":return wrapEditable(<p   key={i} className="prev-square">■&nbsp;&nbsp;{content}</p>);
      case "hr":           return <hr  key={i} className="prev-hr" />;
      case "spacer":       return <div key={i} className="prev-spacer" />;
      case "note":         return wrapEditable(<p key={i} className="prev-note" style={{fontSize:"9px",color:"#888",fontStyle:"italic",margin:"4px 0",paddingLeft:({1:14,2:34,3:54}[section.indent]||0)}}>{content}</p>);
      case "pricing_table":
        if (section.dualYear) {
          const labels = section.yearLabels || ["Year 1","Year 2"];
          return (
            <div key={i} className="prev-pricing-wrap" style={{margin:"16px 0",position:"relative"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
                {section.label
                  ? <div style={{fontWeight:700,fontSize:"12px",fontFamily:"Arial,Helvetica,sans-serif"}}>{section.label}</div>
                  : <span/>}
                <EditTableButton section={section} onEditTable={onEditTable} />
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px",fontFamily:"Arial,Helvetica,sans-serif"}}>
                <thead><tr style={{background:"#1B1464",color:"#fff"}}>
                  <th style={{padding:"4px 6px",textAlign:"left"}}>Module / Description</th>
                  <th style={{padding:"4px 6px",textAlign:"center"}}>Initial Here</th>
                  <th style={{padding:"4px 6px",textAlign:"center"}}>{labels[0]}</th>
                  <th style={{padding:"4px 6px",textAlign:"center"}}>{labels[1]}</th>
                </tr></thead>
                <tbody>
                  {(section.rows||[]).map((row,ri)=>{
                    const y2tot = row.totalYear===2;
                    const cell1 = row.isTotal&&y2tot?"":(row.price||"");
                    const cell2 = row.isTotal&&!y2tot?"":(row.price2||"");
                    return (
                      <tr key={ri} style={{background:row.isTotal?"#E8E8F0":(ri%2?"#f8f8fc":"#fff"),borderBottom:"1px solid #ddd"}}>
                        <td style={{padding:"4px 6px",fontWeight:row.isTotal?700:400}}>
                          {row.module}
                          {row.items&&row.items.length>0&&<div style={{paddingLeft:"12px",fontSize:"9px",color:"#555"}}>{row.items.map((it,ii)=><div key={ii}>{it}</div>)}</div>}
                        </td>
                        <td style={{padding:"4px 6px",textAlign:row.qty?"left":"center",verticalAlign:"bottom",color:row.qty?"#888":"#999"}}>{row.qty?"#: _________":(row.initial?"_________":"")}</td>
                        <td style={{padding:"4px 6px",textAlign:"center",verticalAlign:"bottom",fontWeight:row.isTotal?700:400}}>{cell1}</td>
                        <td style={{padding:"4px 6px",textAlign:"center",verticalAlign:"bottom",fontWeight:row.isTotal?700:400}}>{cell2}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }
        return (
          <div key={i} className="prev-pricing-wrap" style={{margin:"16px 0",position:"relative"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"8px"}}>
              {section.label
                ? <div style={{fontWeight:700,fontSize:"12px",fontFamily:"Arial,Helvetica,sans-serif"}}>{section.label}</div>
                : <span/>}
              <EditTableButton section={section} onEditTable={onEditTable} />
            </div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px",fontFamily:"Arial,Helvetica,sans-serif"}}>
              <thead><tr style={{background:"#1B1464",color:"#fff"}}>
                <th style={{padding:"4px 6px",textAlign:"left"}}>Module / Description</th>
                <th style={{padding:"4px 6px",textAlign:"center"}}>Initial Here</th>
                <th style={{padding:"4px 6px",textAlign:"center"}}>Price</th>
              </tr></thead>
              <tbody>
                {(section.rows||[]).map((row,ri)=>(
                  <tr key={ri} style={{background:row.isTotal?"#E8E8F0":(ri%2?"#f8f8fc":"#fff"),borderBottom:"1px solid #ddd"}}>
                    <td style={{padding:"4px 6px",fontWeight:row.isTotal?700:400}}>
                      {row.module}
                      {row.items&&row.items.length>0&&<div style={{paddingLeft:"12px",fontSize:"9px",color:"#555"}}>{row.items.map((it,ii)=><div key={ii}>{it}</div>)}</div>}
                    </td>
                    <td style={{padding:"4px 6px",textAlign:row.qty?"left":"center",verticalAlign:"bottom",color:row.qty?"#888":"#999"}}>{row.qty?"#: _________":(row.initial?"_________":"")}</td>
                    <td style={{padding:"4px 6px",textAlign:"center",verticalAlign:"bottom",fontWeight:row.isTotal?700:400}}>
                      {onPriceChange?(
                        <input type="text" value={row.price||""} onChange={e=>onPriceChange(i,ri,e.target.value)}
                          style={{width:"80px",textAlign:"center",border:"1px solid #ccc",borderRadius:"3px",padding:"2px 4px",fontSize:"10px",fontWeight:row.isTotal?700:400,fontFamily:"Arial,Helvetica,sans-serif",background:row.isTotal?"#E8E8F0":"#fff"}} placeholder="$0"/>
                      ):row.price}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "data_table": {
        const headers = section.headers || [];
        const rows = section.rows || [];
        const renderCell = (cell) => {
          if (Array.isArray(cell)) {
            return <ul style={{margin:0,paddingLeft:"14px"}}>{cell.map((line,li)=><li key={li} style={{marginBottom:"2px"}}>{line}</li>)}</ul>;
          }
          return cell;
        };
        return (
          <div key={i} className="prev-data-table-wrap" style={{margin:"12px 0"}}>
            {section.label && <div style={{fontWeight:700,fontSize:"12px",marginBottom:"6px",fontFamily:"Arial,Helvetica,sans-serif"}}>{section.label}</div>}
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:"10px",fontFamily:"Arial,Helvetica,sans-serif",border:"1px solid #999"}}>
              {headers.length>0 && (
                <thead><tr style={{background:"#D9E2F3"}}>
                  {headers.map((h,hi)=><th key={hi} style={{padding:"5px 7px",textAlign:"left",border:"1px solid #999",fontWeight:700}}>{h}</th>)}
                </tr></thead>
              )}
              <tbody>
                {rows.map((row,ri)=>{
                  const cells = row.cells || [];
                  return (
                    <tr key={ri}>
                      {cells.map((c,ci)=>(
                        <td key={ci} style={{padding:"5px 7px",border:"1px solid #999",verticalAlign:"top"}}>{renderCell(c)}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      case "pricing_total":
        return wrapEditable(<div key={i} className="prev-pricing-total" style={{fontWeight:700,fontSize:"13px",fontFamily:"Arial,Helvetica,sans-serif",padding:"8px 0",margin:"12px 0",borderTop:"2px solid #000",borderBottom:"2px solid #000"}}>{content}</div>);
      case "bank_of_hours": {
        const rate=section.rate||180, minHours=section.minHours||5;
        return (
          <div key={i} className="prev-bank-card">
            <div className="prev-bank-header">
              <span className="prev-bank-icon" aria-hidden="true">⏱</span>
              <span className="prev-bank-title">Bank of Support Hours</span>
              <span className="prev-bank-rate">${rate}<span className="prev-bank-rate-unit">/hr</span></span>
            </div>
            <div className="prev-bank-meta">{minHours}-hour minimum &nbsp;·&nbsp; Hours never expire &nbsp;·&nbsp; No refunds</div>
            <div className="prev-bank-formula">
              <span className="prev-bank-blank" aria-label="hours blank"/>
              <span className="prev-bank-formula-label">hours</span>
              <span className="prev-bank-op">×</span>
              <span className="prev-bank-amt">${rate}</span>
              <span className="prev-bank-op">=</span>
              <span className="prev-bank-amt">$</span>
              <span className="prev-bank-blank prev-bank-blank-total" aria-label="total blank"/>
            </div>
          </div>
        );
      }
      case "signature":
        return (
          <div key={i} className="prev-sig">
            <div className="prev-sig-two-col">
              <div className="prev-sig-col">
                <div className="prev-sig-label">Client</div>
                <div className="prev-sig-line">By: <span className="prev-sig-blank prev-sig-blank--lg">______________________________</span></div>
                <div className="prev-sig-line">&nbsp;</div>
                <div className="prev-sig-line"><span className="prev-sig-blank prev-sig-blank--md">______________________________</span></div>
                <div className="prev-sig-line">(Print Name)</div>
                <div className="prev-sig-line">Title: <span className="prev-sig-blank prev-sig-blank--md">__________________________</span></div>
                <div className="prev-sig-line">Date: <span className="prev-sig-blank prev-sig-blank--md">___________________________</span></div>
              </div>
              <div className="prev-sig-col">
                <div className="prev-sig-label">Dynamic Health IT, Inc.</div>
                <div className="prev-sig-line">By: <span className="prev-sig-blank prev-sig-blank--lg">______________________________</span></div>
                <div className="prev-sig-line">&nbsp;</div>
                <div className="prev-sig-line">Jeffery P. Robbins</div>
                <div className="prev-sig-line">President</div>
                <div className="prev-sig-line">&nbsp;</div>
                <div className="prev-sig-line">Date: <span className="prev-sig-blank prev-sig-blank--md">___________________________</span></div>
              </div>
            </div>
            {/* Full width beneath both columns — matches the PDF, where the
                mailing address spans the page rather than sitting in a column. */}
            <div className="prev-sig-address">
              <div className="prev-sig-line">Mailing Address:</div>
              <div className="prev-sig-line"><span className="prev-sig-blank prev-sig-blank--full">______________________________________________</span></div>
              <div className="prev-sig-line"><span className="prev-sig-blank prev-sig-blank--full">______________________________________________</span></div>
            </div>
            <div className="prev-ap-contact">
              <div className="prev-sig-label">Client A/P Contact:</div>
              <div className="prev-sig-line">Name: <span className="prev-sig-blank prev-sig-blank--lg">____________________________________________</span></div>
              <div className="prev-sig-line">Phone: <span className="prev-sig-blank prev-sig-blank--sm">__________________________</span>&nbsp;&nbsp;Ex: <span className="prev-sig-blank" style={{width:"12%"}}>____________</span></div>
              <div className="prev-sig-line">Email: <span className="prev-sig-blank prev-sig-blank--lg">____________________________________________</span></div>
            </div>
          </div>
        );
      default: return null;
    } })();
    if (node === null) return null;
    const finalNode = wrapRemovable(node);

    // An insert point after every section except the signature — anything added
    // after that would print below the signature line.
    const anchor = sectionKey(section);
    if (!onInsertAfter || !anchor || section.type === "signature") return finalNode;
    return (
      <Fragment key={`s${i}`}>
        {finalNode}
        <InsertPoint onInsert={(type) => onInsertAfter(anchor, type)} />
      </Fragment>
    );
  });
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const IconCheck   = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none"><polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IconEmpty   = () => <svg width="48" height="48" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="#C5C9D4" strokeWidth="1.5"/><path d="M14 2v6h6M8 13h8M8 17h5" stroke="#C5C9D4" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const IconPlus    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const IconSearch  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const IconTrash   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const IconEdit    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2"/></svg>;
const IconX       = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
const IconSave    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/><path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2"/></svg>;
const IconArrowUp = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IconArrowDown=() => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;

// ─── Edit prompts ────────────────────────────────────────────────────────────

function EditPromptBar({ prompt, onSaveAsSnippet, onUpdateTemplate, onUpdateSnippet, onAddToTemplate, onDismiss, addTargets }) {
  const [snippetName, setSnippetName] = useState("");
  const [mode, setMode] = useState(null); // null | "snippet" | "template" | "updateSnippet" | "addToTemplate"
  const [addTarget, setAddTarget] = useState(addTargets?.[0]?.id || "");
  // "Update Template Default" only works for regular template sections, not
  // hosting/UDS sub-arrays — and not for a bullet generated per agreement from a
  // measure, whose template section is the generator, not the text on screen.
  const isTemplateSection = !!prompt.section?._srcProductId && !prompt.section?._srcSubType
    && !prompt.section?._dynamicExpanded;
  const hasSourceSnippet  = !!prompt.section?._snippetId;
  // An inserted (not-yet-in-any-template) section can be pushed INTO a template.
  const isInsertedSection = prompt.section?._customIdx !== undefined && !!onAddToTemplate && (addTargets || []).length > 0;

  if (mode === "addToTemplate") {
    return (
      <div className="edit-prompt-bar">
        <span className="edit-prompt-label">Add this section to which template?</span>
        <select className="input edit-prompt-input" value={addTarget} onChange={e => setAddTarget(e.target.value)} style={{maxWidth:260}}>
          {addTargets.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="btn-sm btn-sm-accent" onClick={() => onAddToTemplate(addTarget)}>Add to Template</button>
        <button className="btn-sm btn-sm-ghost" onClick={() => setMode(null)}>Cancel</button>
      </div>
    );
  }
  if (mode === "snippet") {
    return (
      <div className="edit-prompt-bar">
        <span className="edit-prompt-label">Name this snippet:</span>
        <input className="input edit-prompt-input" autoFocus placeholder="e.g. Updated assumption clause"
          value={snippetName} onChange={e => setSnippetName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") onSaveAsSnippet(snippetName); }} />
        <button className="btn-sm btn-sm-accent" onClick={() => onSaveAsSnippet(snippetName)}>Save Snippet</button>
        <button className="btn-sm btn-sm-ghost" onClick={() => setMode(null)}>Back</button>
      </div>
    );
  }
  if (mode === "template") {
    return (
      <div className="edit-prompt-bar">
        <span className="edit-prompt-label">Update this section in the agreement template for everyone?</span>
        <button className="btn-sm btn-sm-accent" onClick={onUpdateTemplate}>Yes, Update Template</button>
        <button className="btn-sm btn-sm-ghost" onClick={() => setMode(null)}>Cancel</button>
      </div>
    );
  }
  if (mode === "updateSnippet") {
    return (
      <div className="edit-prompt-bar">
        <span className="edit-prompt-label">Overwrite the source snippet with your edits?</span>
        <button className="btn-sm btn-sm-accent" onClick={onUpdateSnippet}>Yes, Update Snippet</button>
        <button className="btn-sm btn-sm-ghost" onClick={() => setMode(null)}>Cancel</button>
      </div>
    );
  }
  return (
    <div className="edit-prompt-bar">
      <span className="edit-prompt-label">Edit saved. Do you want to…</span>
      {hasSourceSnippet && <button className="btn-sm btn-sm-accent" onClick={() => setMode("updateSnippet")}>Update Current Snippet</button>}
      <button className="btn-sm btn-sm-accent" onClick={() => setMode("snippet")}>Save as New Snippet</button>
      {isTemplateSection && <button className="btn-sm btn-sm-accent" onClick={() => setMode("template")}>Update Template Default</button>}
      {isInsertedSection && <button className="btn-sm btn-sm-accent" onClick={() => setMode("addToTemplate")}>Add to Template</button>}
      <button className="btn-sm btn-sm-ghost" onClick={onDismiss}>Just This Agreement</button>
    </div>
  );
}

// Status only. Creating a template used to happen here ("Save as New Agreement
// Template"), which was a second, hidden way to author templates: it cloned the
// product and baked in whatever edits happened to be on screen. Templates are now
// made explicitly with "+ New template", and each edit decides for itself whether
// it belongs to the template ("Update Template Default" / "Add to Template" /
// "Remove from Template") or just this agreement.
function AgreementEditsBar({ count }) {
  return (
    <div className="edits-bar">
      <span className="edits-bar-label">
        {count} edit{count!==1?"s":""} from the base template — applies to this agreement only unless you save it to the template.
      </span>
    </div>
  );
}

// ─── CommentsReviewPage ───────────────────────────────────────────────────────
// Team-facing screen: full document on left, comment threads on right.

// Collect every visible text string from a section so we can match comment anchors
// against table cells, prices, bullets, etc. — not just section.content.
function sectionTexts(section, fields) {
  const f = (s) => fill(s || '', fields);
  const out = [f(section.content), f(section.label)];
  for (const row of section.rows || []) {
    out.push(f(row.module), row.price || '', row.price2 || '');
    for (const item of row.items || []) out.push(f(item));
    for (const cell of row.cells || []) {
      if (Array.isArray(cell)) cell.forEach(l => out.push(f(l)));
      else out.push(f(cell));
    }
  }
  for (const h of section.headers || []) out.push(f(h));
  return out.map(t => (t || '').toLowerCase());
}

function sectionHits(section, fields, previews) {
  if (!previews || previews.length === 0) return { any: false, active: false };
  const texts = sectionTexts(section, fields);
  const hit = (p) => p && texts.some(t => t.includes(p.toLowerCase().slice(0, 60)));
  return {
    any:    previews.some(p => hit(p)),
    active: previews.length > 0 && hit(previews[0]), // previews[0] = active preview
  };
}

// Returns highlight style for a specific cell value against the active preview.
function cellHl(value, activePreview) {
  if (!activePreview || !value) return {};
  return (value + '').toLowerCase().includes(activePreview.toLowerCase().slice(0, 60))
    ? { background: '#FFD600', fontWeight: 700 }
    : {};
}

function renderDocSection(section, fields, allPreviews, activePreview) {
  // allPreviews = every comment's sectionPreview (for subtle always-on markers)
  // activePreview = the clicked comment's sectionPreview (for bold highlight)
  const { any: hasAny, active: isActive } = sectionHits(section, fields,
    activePreview ? [activePreview, ...allPreviews] : allPreviews);

  const content = fill(section.content || '', fields);

  // Wrap text-based sections in a highlight container
  const hl = isActive
    ? { background: '#FFF9C4', borderLeft: '4px solid #F9A825', paddingLeft: 8, borderRadius: 2 }
    : hasAny
    ? { borderLeft: '3px solid #fcd34d', paddingLeft: 8 }
    : {};

  const inlineHl = isActive ? { background: '#FFD600', borderRadius: 2 } : {};

  switch (section.type) {
    case 'doc_header':
      return <div style={{ borderBottom: '3px solid #2872FA', paddingBottom: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div style={{ fontWeight: 700, fontSize: 16, fontStyle: 'italic' }}>Dynamic Health IT, Inc.</div></div>;
    case 'title':        return <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 700, marginBottom: 6, ...hl }}>{content}</div>;
    case 'title_italic': return <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 700, fontStyle: 'italic', marginBottom: 6, ...hl }}>{content}</div>;
    case 'subtitle':     return <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 600, color: '#555', marginBottom: 6, ...hl }}>{content}</div>;
    case 'heading':      return <div style={{ fontSize: 15, fontWeight: 700, textDecoration: 'underline', marginTop: 20, marginBottom: 8, ...hl }}>{content}</div>;
    case 'paragraph':    return <p style={{ margin: '0 0 8px', textIndent: 24, fontSize: 13, lineHeight: 1.7, ...hl }}>{content}</p>;
    case 'paragraph_noindent': return <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.7, ...hl }}>{content}</p>;
    case 'bullet':         return <div style={{ paddingLeft: 20, marginBottom: 4, fontSize: 13, lineHeight: 1.6, ...hl }}>• &nbsp;{content}</div>;
    case 'diamond_bullet': return <div style={{ paddingLeft: 20, marginBottom: 6, fontSize: 13, lineHeight: 1.6, ...hl }}>❖ &nbsp;{content}</div>;
    case 'arrow_bullet':   return <div style={{ paddingLeft: 48, marginBottom: 4, fontSize: 13, lineHeight: 1.6, ...hl }}>➢ &nbsp;{content}</div>;
    case 'square_bullet':  return <div style={{ paddingLeft: 76, marginBottom: 4, fontSize: 13, lineHeight: 1.6, ...hl }}>■ &nbsp;{content}</div>;
    case 'note': return <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', margin: '4px 0', lineHeight: 1.5, ...hl }}>{content}</div>;
    case 'hr':     return <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '12px 0' }} />;
    case 'spacer': return <div style={{ height: 20 }} />;
    case 'pricing_total': return <div style={{ fontWeight: 700, fontSize: 14, padding: '8px 0', margin: '12px 0', borderTop: '2px solid #000', borderBottom: '2px solid #000', ...hl }}>{content}</div>;
    case 'data_table': {
      const headers = section.headers || [];
      const rows = section.rows || [];
      return (
        <div style={{ margin: '12px 0' }}>
          {section.label && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{section.label}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, border: '1px solid #999' }}>
            {headers.length > 0 && <thead><tr style={{ background: '#D9E2F3' }}>{headers.map((h, i) => <th key={i} style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid #999', ...cellHl(h, activePreview) }}>{h}</th>)}</tr></thead>}
            <tbody>{rows.map((row, ri) => <tr key={ri}>{(row.cells || []).map((c, ci) => {
              const cellText = Array.isArray(c) ? c.join(' ') : c;
              return <td key={ci} style={{ padding: '6px 8px', border: '1px solid #999', verticalAlign: 'top', ...cellHl(cellText, activePreview) }}>{Array.isArray(c) ? <ul style={{ margin: 0, paddingLeft: 16 }}>{c.map((l, li) => <li key={li}>{l}</li>)}</ul> : c}</td>;
            })}</tr>)}</tbody>
          </table>
        </div>
      );
    }
    case 'pricing_table': {
      const rows = section.rows || [];
      // A comment bubble in the margin if any comment touches this table
      return (
        <div style={{ marginBottom: 16, position: 'relative', borderLeft: isActive ? '4px solid #F9A825' : hasAny ? '3px solid #fcd34d' : 'none', paddingLeft: (isActive || hasAny) ? 8 : 0 }}>
          {section.label && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{section.label}</div>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#1B1464', color: '#fff' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', width: '60%' }}>Module / Description</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', width: '20%' }}>Initial</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', width: '20%' }}>Price</th>
            </tr></thead>
            <tbody>{rows.map((row, ri) => {
              const modHl  = cellHl(fill(row.module || '', fields), activePreview);
              const priceHl = cellHl(row.price, activePreview);
              const rowBase = row.isTotal ? '#E8E8F0' : ri % 2 === 0 ? '#fff' : '#f9f9f9';
              return (
                <tr key={ri} style={{ background: rowBase, borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '8px 10px', fontWeight: row.isTotal ? 700 : 400, ...modHl }}>{fill(row.module || '', fields)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center', color: '#aaa', fontSize: 11 }}>{row.initial ? '[ initial ]' : ''}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: row.isTotal ? 700 : 400, ...priceHl }}>{row.price || ''}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      );
    }
    case 'bank_of_hours':
      return <div style={{ border: '1px solid #d8dcee', borderRadius: 8, background: '#f8f9ff', padding: '14px 16px', margin: '12px 0', fontSize: 13, ...inlineHl }}>⏱ <strong>Prepaid Bank of Support Hours</strong> — ${section.rate || 180}/hr</div>;
    case 'signature':
      return (
        <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid #ddd' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Client</div>
              {['By (Signature)', 'Print Name', 'Title', 'Date', 'Mailing Address'].map(l => (
                <div key={l} style={{ fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>{l}: <span style={{ color: '#aaa' }}>____________________</span></div>
              ))}
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Dynamic Health IT, Inc.</div>
              <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>By: ____________________________</div>
              <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>Jeffery P. Robbins — President</div>
              <div style={{ fontSize: 13, padding: '5px 0', borderBottom: '1px solid #f0f0f0' }}>Date: _______________________</div>
            </div>
          </div>
        </div>
      );
    default: return null;
  }
}

function CommentsReviewPage({ token, user, onBack }) {
  const [draft,         setDraft]         = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [responses,     setResponses]     = useState({});
  const [saving,        setSaving]        = useState(null);
  const [resolvingId,   setResolvingId]   = useState(null);
  const [resolveAll,    setResolveAll]    = useState(false);
  const [allResolved,   setAllResolved]   = useState(false);
  const [error,         setError]         = useState('');
  const [activeId,      setActiveId]      = useState(null);
  const [reopening,     setReopening]     = useState(false);
  const [archiveOpen,   setArchiveOpen]   = useState(false);

  useEffect(() => {
    fetch(`/api/draft/${token}`)
      .then(r => r.json())
      .then(d => { setDraft(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  // Normalize old flat-format comment to thread format
  const migrateCmt = (c) => {
    if (c.thread) return c;
    const thread = [{ from: 'client', text: c.text || '', at: c.createdAt }];
    if (c.teamResponse) thread.push({ from: 'team', text: c.teamResponse, at: c.respondedAt, by: c.respondedBy });
    return { ...c, thread, resolved: !!c.teamResponse, resolvedBy: c.teamResponse ? 'team' : null, resolvedAt: c.teamResponse ? c.respondedAt : null };
  };

  const rawComments    = draft?.clientComments || [];
  const comments       = rawComments.map(migrateCmt);
  const archivedComments = (draft?.archivedComments || []).map(migrateCmt);
  const unresolvedCount = comments.filter(c => !c.resolved).length;
  const allAnswered     = unresolvedCount === 0 && comments.length > 0;
  const activeComment   = comments.find(c => c.id === activeId) || null;

  const handleSendReply = async (commentId) => {
    const text = (responses[commentId] || '').trim();
    if (!text) return;
    setSaving(commentId); setError('');
    try {
      const res = await fetch(`/api/draft/${token}/comments/${commentId}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ response: text }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Send failed');
      setDraft(d => ({
        ...d,
        clientComments: d.clientComments.map(c => {
          if (c.id !== commentId) return c;
          const m = migrateCmt(c);
          return { ...m, thread: [...m.thread, { from: 'team', text, at: new Date().toISOString(), by: user?.name || 'Team' }], teamResponse: text, respondedBy: user?.name || 'Team', respondedAt: new Date().toISOString() };
        }),
      }));
      setResponses(prev => { const n = { ...prev }; delete n[commentId]; return n; });
    } catch (err) { setError(err.message); }
    finally { setSaving(null); }
  };

  const handleResolveComment = async (commentId) => {
    setResolvingId(commentId); setError('');
    try {
      const res = await fetch(`/api/draft/${token}/comments/${commentId}/resolve`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      const { allResolved: ar } = await res.json().catch(() => ({}));
      setDraft(d => ({
        ...d,
        clientComments: d.clientComments.map(c => c.id === commentId
          ? { ...migrateCmt(c), resolved: true, resolvedBy: 'team', resolvedAt: new Date().toISOString() }
          : c
        ),
      }));
      if (ar) setAllResolved(true);
    } catch (err) { setError(err.message); }
    finally { setResolvingId(null); }
  };

  const handleResolveAll = async () => {
    setResolveAll(true); setError('');
    try {
      const res = await fetch(`/api/draft/${token}/comments/resolve`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      setAllResolved(true);
    } catch (err) { setError(err.message); }
    finally { setResolveAll(false); }
  };

  const handleEditAgreement = async () => {
    setReopening(true);
    try {
      const res = await fetch(`/api/draft/${token}/reopen`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      window.location.href = `/draft/${token}`;
    } catch (err) { setError(err.message); setReopening(false); }
  };

  const bS = (bg, bd = bg, cl = '#fff') => ({ padding: '8px 18px', background: bg, border: `1px solid ${bd}`, borderRadius: 6, color: cl, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'Arial, sans-serif' });
  const fmtTime = (at) => at ? new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading…</div>;

  if (!draft) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1a3a6e', marginBottom: 12 }}>Agreement not found</div>
        <button onClick={onBack} style={bS('#1a3a6e')}>← Back to Home</button>
      </div>
    </div>
  );

  if (allResolved) return (
    <div style={{ minHeight: '100vh', background: '#f4f5f7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '40px 36px', maxWidth: 480, textAlign: 'center', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#1a6e3c', marginBottom: 10 }}>All comments resolved</div>
        <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 24 }}>
          The client has been emailed and can now return to sign the agreement.
        </p>
        <button onClick={onBack} style={bS('#1a3a6e')}>← Back to Home</button>
      </div>
    </div>
  );

  const sections = draft.sections || [];
  const fields   = draft.fields   || {};

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', fontFamily: 'Arial, sans-serif' }}>

      {/* Header */}
      <div style={{ background: '#1a3a6e', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 12, height: 60, boxShadow: '0 2px 8px rgba(0,0,0,0.2)', flexShrink: 0 }}>
        <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6, color: '#fff', fontSize: 13, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 }}>← Home</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Client Comments — {draft.fields?.CUSTOMER_NAME || 'Agreement'}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
            {comments.length} comment{comments.length !== 1 ? 's' : ''} · {unresolvedCount > 0 ? `${unresolvedCount} open` : 'All resolved'}
          </div>
        </div>
        <button
          onClick={handleEditAgreement}
          disabled={reopening}
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, color: '#fff', fontSize: 13, padding: '6px 14px', cursor: 'pointer', flexShrink: 0, opacity: reopening ? 0.6 : 1 }}
        >
          {reopening ? 'Opening…' : '✎ Edit Agreement'}
        </button>
      </div>

      {/* Two-panel body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Left — full document */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#f4f5f7', padding: 24 }}>
          <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: 10, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            {sections.map((s, i) => (
              <div key={i}>{renderDocSection(s, fields,
                comments.map(c => c.sectionPreview).filter(Boolean),
                activeComment?.sectionPreview || null,
              )}</div>
            ))}
          </div>
        </div>

        {/* Right — IM-style comment threads */}
        <div style={{ width: 400, background: '#fff', borderLeft: '1px solid #e8e8e8', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {/* Archived comment rounds */}
            {archivedComments.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <button
                  onClick={() => setArchiveOpen(o => !o)}
                  style={{ width: '100%', background: '#f4f5f7', border: '1px solid #e0e0e0', borderRadius: 6, padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#888', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>📁 Prior rounds ({archivedComments.length} comment{archivedComments.length !== 1 ? 's' : ''})</span>
                  <span>{archiveOpen ? '▲' : '▼'}</span>
                </button>
                {archiveOpen && (
                  <div style={{ marginTop: 6 }}>
                    {archivedComments.map((c, idx) => (
                      <div key={c.id || idx} style={{ borderRadius: 8, marginBottom: 8, border: '1px solid #e8e8e8', background: '#fafafa', overflow: 'hidden', opacity: 0.75 }}>
                        <div style={{ padding: '7px 12px', borderBottom: '1px solid #f0f0f0', background: '#f4f5f7' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa' }}>
                            Round {c.round || '?'} · Comment {idx + 1}
                            {c.resolved && <span style={{ color: '#2e7d32', marginLeft: 6 }}>✓ Resolved</span>}
                          </div>
                          {c.sectionPreview && (
                            <div style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic', borderLeft: '2px solid #ddd', paddingLeft: 6, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              &ldquo;{c.sectionPreview.slice(0, 70)}{c.sectionPreview.length > 70 ? '…' : ''}&rdquo;
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '8px 12px' }}>
                          {(c.thread || []).map((msg, mi) => (
                            <div key={mi} style={{ display: 'flex', justifyContent: msg.from === 'team' ? 'flex-end' : 'flex-start', marginBottom: 6 }}>
                              <div style={{ maxWidth: '82%', background: msg.from === 'team' ? '#f0f4fa' : '#fdf8ee', border: `1px solid ${msg.from === 'team' ? '#d0dff0' : '#f0dfa0'}`, borderRadius: 8, padding: '5px 9px' }}>
                                <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4 }}>{msg.text}</div>
                                <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>{fmtTime(msg.at)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {error && <div style={{ background: '#fdecea', color: '#c62828', border: '1px solid #f5c6cb', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {comments.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>No comments found.</div>
            ) : comments.map((c, idx) => {
              const isActive  = c.id === activeId;
              const thread    = c.thread || [];
              const draftText = responses[c.id] || '';
              return (
                <div
                  key={c.id}
                  style={{
                    borderRadius: 8, marginBottom: 12,
                    border: `1.5px solid ${c.resolved ? '#c8e6c9' : isActive ? '#F9A825' : '#eee'}`,
                    background: c.resolved ? '#f9fff9' : isActive ? '#fffdf5' : '#fff',
                    boxShadow: isActive ? '0 0 0 2px rgba(249,168,37,0.15)' : '0 1px 4px rgba(0,0,0,0.04)',
                    overflow: 'hidden', transition: 'border-color 0.15s',
                  }}
                >
                  {/* Comment header — click to highlight in doc */}
                  <div
                    onClick={() => setActiveId(isActive ? null : c.id)}
                    style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: c.resolved ? '#2e7d32' : '#b45309', marginBottom: 3 }}>
                        {c.resolved ? '✓ Resolved' : `Comment ${idx + 1}`}
                        {c.round > 1 && <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 6 }}>Round {c.round}</span>}
                      </div>
                      {c.sectionPreview && (
                        <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic', borderLeft: '2px solid #F9A825', paddingLeft: 6, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          &ldquo;{c.sectionPreview.slice(0, 70)}{c.sectionPreview.length > 70 ? '…' : ''}&rdquo;
                        </div>
                      )}
                    </div>
                    {c.resolved && c.resolvedBy && (
                      <span style={{ fontSize: 10, color: '#888', flexShrink: 0 }}>by {c.resolvedBy}</span>
                    )}
                  </div>

                  {/* Thread messages */}
                  <div style={{ padding: '10px 12px' }} onClick={e => e.stopPropagation()}>
                    {thread.map((msg, mi) => (
                      <div key={mi} style={{ display: 'flex', justifyContent: msg.from === 'team' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                        <div style={{
                          maxWidth: '82%',
                          background: msg.from === 'team' ? '#e3f2fd' : '#fff8e1',
                          border: `1px solid ${msg.from === 'team' ? '#90caf9' : '#fcd34d'}`,
                          borderRadius: msg.from === 'team' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                          padding: '7px 10px',
                        }}>
                          {msg.from === 'team' && msg.by && (
                            <div style={{ fontSize: 10, color: '#1565c0', fontWeight: 600, marginBottom: 2 }}>{msg.by}</div>
                          )}
                          <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.text}</div>
                          <div style={{ fontSize: 10, color: '#aaa', marginTop: 3, textAlign: msg.from === 'team' ? 'right' : 'left' }}>{fmtTime(msg.at)}</div>
                        </div>
                      </div>
                    ))}

                    {/* Reply + resolve area (hidden when resolved) */}
                    {!c.resolved && (
                      <>
                        <textarea
                          value={draftText}
                          onChange={e => setResponses(prev => ({ ...prev, [c.id]: e.target.value }))}
                          placeholder="Reply to this comment…"
                          rows={2}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1.5px solid #e0e0e0', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'Arial, sans-serif', marginTop: 4, marginBottom: 6, outline: 'none' }}
                          onFocus={e => e.target.style.borderColor = '#1a3a6e'}
                          onBlur={e => e.target.style.borderColor = '#e0e0e0'}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => handleResolveComment(c.id)}
                            disabled={resolvingId === c.id}
                            style={{ padding: '6px 10px', background: '#fff', border: '1px solid #ccc', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#555', cursor: 'pointer', flexShrink: 0, opacity: resolvingId === c.id ? 0.5 : 1 }}
                          >
                            {resolvingId === c.id ? '…' : '✓ Mark Resolved'}
                          </button>
                          <button
                            onClick={() => handleSendReply(c.id)}
                            disabled={!draftText.trim() || saving === c.id}
                            style={{ flex: 1, padding: '6px 10px', background: '#1a3a6e', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', opacity: !draftText.trim() || saving === c.id ? 0.5 : 1 }}
                          >
                            {saving === c.id ? 'Sending…' : 'Send Reply →'}
                          </button>
                        </div>
                      </>
                    )}
                    {c.resolved && (
                      <div style={{ fontSize: 11, color: '#2e7d32', textAlign: 'center', padding: '4px 0' }}>
                        ✓ Resolved{c.resolvedAt ? ` · ${fmtTime(c.resolvedAt)}` : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer — resolve all shortcut */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', flexShrink: 0 }}>
            <button
              onClick={handleResolveAll}
              disabled={!allAnswered || resolveAll}
              style={{ ...bS(allAnswered ? '#1a6e3c' : '#ccc', allAnswered ? '#1a6e3c' : '#ccc'), width: '100%', padding: '10px 0', fontSize: 14, opacity: !allAnswered || resolveAll ? 0.6 : 1 }}
            >
              {resolveAll ? 'Resolving…' : 'Resolve All & Re-Send to Client →'}
            </button>
            {!allAnswered && (
              <div style={{ fontSize: 12, color: '#aaa', textAlign: 'center', marginTop: 6 }}>
                {unresolvedCount} open comment{unresolvedCount !== 1 ? 's' : ''} — reply or mark each resolved
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {

  // Auth & routing
  const [authChecked,     setAuthChecked]     = useState(false);
  const [currentUser,     setCurrentUser]     = useState(null);
  const [screen,          setScreen]          = useState("home");
  const [reviewMode,      setReviewMode]      = useState(false);
  const [reviewToken,     setReviewToken]     = useState(null);
  const [sendMode,        setSendMode]        = useState(false);
  const [sendToken,       setSendToken]       = useState(null);
  const [sendDone,        setSendDone]        = useState(false);
  const [overrideSend,    setOverrideSend]    = useState(false);
  const [sendDraft,       setSendDraft]       = useState(null);
  const [resendingJeff,   setResendingJeff]   = useState(false);
  const [signMode,        setSignMode]        = useState(false);
  const [signClientToken, setSignClientToken] = useState(null);
  const [countersignMode, setCountersignMode] = useState(false);
  const [jeffToken,       setJeffToken]       = useState(null);
  const [commentsMode,    setCommentsMode]    = useState(false);
  const [commentsToken,   setCommentsToken]   = useState(null);

  // Builder
  const [products,        setProducts]        = useState([]);
  const [selectedIds,     setSelectedIds]     = useState(new Set());
  const [fields,          setFields]          = useState({});
  const [toast,           setToast]           = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [customSections,  setCustomSections]  = useState([]);
  const [sectionOverrides,setSectionOverrides]= useState({}); // { "<productId>:<sectionIdx>": "edited content" }
  const [hiddenSrcKeys,   setHiddenSrcKeys]   = useState(new Set()); // template/hosting/uds _srcKeys to hide
  const [hostedProducts,  setHostedProducts]  = useState(new Set());
  const [udsProducts,     setUdsProducts]     = useState(new Set());
  const [priceOverrides,  setPriceOverrides]  = useState({});
  // Whole pricing tables hand-edited for THIS agreement — { "<srcKey>": { label, rows } }.
  // priceOverrides only ever moves a price; this replaces the row list, which is
  // the only way to add, drop or reword a line without touching the template.
  const [tableOverrides,  setTableOverrides]  = useState({});
  const [disabledAddons,  setDisabledAddons]  = useState(new Set());

  // ── Agreement title ─────────────────────────────────────────────────────────
  // Names the document: the running page header, the download filename, and —
  // for a single-module agreement — the title printed under the customer name.
  // Defaults to the template name and follows the selection until it's edited,
  // after which the typed value sticks (titleTouched).
  const [agreementTitle, setAgreementTitle] = useState("");
  const [titleTouched,   setTitleTouched]   = useState(false);

  // Sidebar (Luke: visible for all modules, not just custom)
  const [library,         setLibrary]         = useState([]);
  const [snippets,        setSnippets]        = useState([]);
  const [libSearch,       setLibSearch]       = useState("");
  const [libTab,          setLibTab]          = useState("library");
  const [libSourceFilter, setLibSourceFilter] = useState("all");
  const [libSectionFilter,setLibSectionFilter]= useState("all");   // Luke
  const [libHighlightId,  setLibHighlightId]  = useState(null);    // Luke
  const [editSnippet,     setEditSnippet]     = useState(null);
  const editNameRef = useRef(null);

  // Inline preview editing — editKey is "c:<customIdx>" or "t:<productId>:<sectionIdx>"
  const [previewEditKey,     setPreviewEditKey]     = useState(null);
  const [previewEditContent, setPreviewEditContent] = useState("");
  const [editPrompt, setEditPrompt] = useState(null); // {key, content, section, originalContent}

  // Resizable panel widths
  const [infoPanelWidth,    setInfoPanelWidth]    = useState(270);
  const [modulePanelWidth,  setModulePanelWidth]  = useState(260);
  const [sidebarPanelWidth, setSidebarPanelWidth] = useState(340);
  const resizeDrag = useRef(null);

  useEffect(() => {
    const MIN = 150;
    const onMove = (e) => {
      if (!resizeDrag.current) return;
      const { panel, startX, startWidth } = resizeDrag.current;
      const delta = e.clientX - startX;
      if (panel === 'info')    setInfoPanelWidth(   Math.max(MIN, startWidth + delta));
      if (panel === 'module')  setModulePanelWidth( Math.max(MIN, startWidth + delta));
      if (panel === 'sidebar') setSidebarPanelWidth(Math.max(MIN, startWidth - delta));
    };
    const onUp = () => { resizeDrag.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
  }, []);

  const startResize = useCallback((panel, currentWidth) => (e) => {
    e.preventDefault();
    resizeDrag.current = { panel, startX: e.clientX, startWidth: currentWidth };
  }, []);

  const sidebarVisible = selectedIds.size > 0;  // Luke: sidebar for all modules

  const showToast = useCallback((msg, type="success") => {
    setToast({msg, type});
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Auth + URL routing
  useEffect(() => {
    const path = window.location.pathname;
    const reviewMatch = path.match(/^\/review\/([a-f0-9]+)$/);
    if (reviewMatch) { setReviewMode(true); setReviewToken(reviewMatch[1]); sessionStorage.setItem("bossId", new URLSearchParams(window.location.search).get("boss")||"boss1"); setScreen("builder"); }
    const sendMatch = path.match(/^\/send\/([a-f0-9]+)$/);
    if (sendMatch) { setSendMode(true); setSendToken(sendMatch[1]); setScreen("builder"); setOverrideSend(new URLSearchParams(window.location.search).get("override")==="1"); }
    const signMatch = path.match(/^\/sign\/([a-f0-9]+)$/);
    if (signMatch) { setSignMode(true); setSignClientToken(signMatch[1]); }
    const countersignMatch = path.match(/^\/countersign\/([a-f0-9]+)$/);
    if (countersignMatch) { setCountersignMode(true); setJeffToken(countersignMatch[1]); }
    const draftMatch = path.match(/^\/draft\/([a-f0-9]+)$/);
    if (draftMatch) { setSavedDraftToken(draftMatch[1]); setScreen("builder"); }
    const commentsMatch = path.match(/^\/comments\/([a-f0-9]+)$/);
    if (commentsMatch) { setCommentsMode(true); setCommentsToken(commentsMatch[1]); }
    fetch("/api/auth/me").then(r=>r.json()).then(d=>{ if(d.authenticated) setCurrentUser(d.user); setAuthChecked(true); }).catch(()=>setAuthChecked(true));
  }, []);

  // Check whether HubSpot is configured once logged in (no company pre-load)
  useEffect(()=>{
    if(!currentUser) return;
    fetch('/api/hubspot/companies').then(r=>r.json()).then(d=>{if(d.configured===false)setHsConfigured(false);}).catch(()=>{});
  },[currentUser]);

  // Load products
  useEffect(() => {
    fetch("/api/products").then(r=>r.json()).then(d=>{setProducts(d);setLoadingProducts(false);}).catch(()=>{setLoadingProducts(false);showToast("Could not connect to server.","error");});
  }, [showToast]);

  // ── Pricing-table row editing ───────────────────────────────────────────────
  // Opens the row editor for the table clicked in the preview. It can write to
  // either scope: this agreement (parked in tableOverrides, below) or the
  // TEMPLATE, in which case the product list is reloaded and the preview
  // recomposes from the new rows.
  const [tableEditor, setTableEditor] = useState(null); // { productId, productName, sectionIdx, sub, label, rows, agreementRows, srcKey, composedIdx, dynamic }

  const handleEditTable = useCallback((section) => {
    const loc = templateTableLocator(section);
    if (!loc) return;
    const prod = products.find(p => p.id === loc.productId);
    setTableEditor({
      productId:   loc.productId,
      productName: prod?.name || section._module || loc.productId,
      sectionIdx:  loc.sectionIdx,
      sub:         subFromLocator(loc),
      label:       section.label || "",
      // Dual-year tables (registry) price each row twice; the editor needs to
      // know so it shows the second column instead of silently dropping it.
      dualYear:    !!section.dualYear,
      yearLabels:  section.yearLabels,
      // Two row lists, one per scope. The TEMPLATE's rows are what a template
      // save writes back. The agreement's rows are what the preview is actually
      // showing — price overrides applied and dynamic measure rows expanded —
      // which is what "this agreement only" edits. _rowsAll rather than .rows so
      // an add-on the rep happened to uncheck isn't quietly deleted by an edit.
      rows:        sectionListFor(prod, loc)?.[loc.sectionIdx]?.rows || section.rows || [],
      agreementRows: section._rowsAll || section.rows || [],
      srcKey:      section._srcKey,
      composedIdx: section._composedIdx,
      dynamic:     !!section.dynamicRows,
    });
  }, [products]);

  // "Apply to this agreement" — park the rows against this section's srcKey. The
  // prices are now part of those rows, so the inline price overrides for this
  // table are dropped rather than left to re-apply themselves by position.
  const handleTableAppliedToAgreement = useCallback((rows, label) => {
    const t = tableEditor;
    setTableEditor(null);
    if (!t?.srcKey) return;
    setTableOverrides(prev => ({ ...prev, [t.srcKey]: { label, rows } }));
    if (Number.isInteger(t.composedIdx)) {
      const prefix = `${t.composedIdx}__`;
      setPriceOverrides(prev => Object.fromEntries(Object.entries(prev).filter(([k]) => !k.startsWith(prefix))));
    }
    showToast("Pricing table updated for this agreement only", "success");
  }, [tableEditor, showToast]);

  const handleTableOverrideReset = useCallback(() => {
    const t = tableEditor;
    setTableEditor(null);
    if (!t?.srcKey) return;
    setTableOverrides(prev => { const n = { ...prev }; delete n[t.srcKey]; return n; });
    showToast("Table reset — it follows the template again", "success");
  }, [tableEditor, showToast]);

  const handleTableSaved = useCallback(async (_rows, assigned) => {
    try {
      const fresh = await fetch("/api/products").then(r => r.json());
      setProducts(fresh);
      const newIds = (assigned || []).length;
      showToast(newIds ? `Pricing table saved — ${newIds} new row id${newIds !== 1 ? "s" : ""} assigned` : "Pricing table saved to template", "success");
    } catch {
      showToast("Saved, but the product list could not be refreshed — reload the page.", "error");
    }
    setTableEditor(null);
  }, [showToast]);

  // ── Draft from notes ────────────────────────────────────────────────────────
  // The modal proposes; this applies. Price overrides are the awkward part:
  // priceOverrides is keyed by position in the COMPOSED sections, which don't
  // exist until the new selection has rendered. So they're parked by rowId and
  // an effect below converts them once the composition catches up.
  const [notesOpen, setNotesOpen] = useState(false);
  const [assistEnabled, setAssistEnabled] = useState(false);
  const [pendingPrices, setPendingPrices] = useState(null); // [{rowId, price}]

  useEffect(() => {
    fetch("/api/assist/status")
      .then(r => r.ok ? r.json() : null)
      .then(d => setAssistEnabled(!!d?.enabled))
      .catch(() => setAssistEnabled(false));
  }, []);

  const applyNotesDraft = useCallback((d) => {
    // A fresh selection invalidates edits keyed to the old one — same reasoning
    // as toggleProduct.
    setSectionOverrides({});
    setCustomSections([]);
    setHiddenSrcKeys(new Set());
    setEditPrompt(null);
    setPriceOverrides({});
    setTableOverrides({});

    setSelectedIds(new Set(d.products));
    setHostedProducts(new Set(d.hosting));
    setUdsProducts(new Set(d.uds));
    setDisabledAddons(new Set(d.excludeAddons));
    if (d.fields.length) setFields(prev => {
      const next = { ...prev };
      for (const f of d.fields) next[f.key] = f.value;
      return next;
    });
    if (d.agreementTitle) { setTitleTouched(true); setAgreementTitle(d.agreementTitle); }
    setPendingPrices(d.priceOverrides.length ? d.priceOverrides : null);

    setNotesOpen(false);
    const n = d.products.length;
    showToast(n ? `Draft applied — ${n} module${n !== 1 ? "s" : ""}. Check it over before sending.` : "Nothing to apply", n ? "success" : "error");
  }, [showToast]);

  // ── New agreement template (clone or from scratch) ──────────────────────────
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);

  const handleTemplateCreated = useCallback(async (product, remappedRowIds) => {
    try {
      const fresh = await fetch("/api/products").then(r => r.json());
      setProducts(fresh);
      // Select the new template so it drops straight into the preview to edit.
      setSelectedIds(prev => { const n = new Set(prev); n.add(product.id); return n; });
      showToast(
        remappedRowIds
          ? `"${product.name}" created — ${remappedRowIds} pricing row id${remappedRowIds !== 1 ? "s" : ""} re-keyed so it can't clash with the original`
          : `"${product.name}" created — edit it in the preview`,
        "success"
      );
    } catch {
      showToast("Created, but the library could not be refreshed — reload the page.", "error");
    }
    setNewTemplateOpen(false);
  }, [showToast]);

  const handleDeleteProduct = useCallback(async (p) => {
    if (!window.confirm(`Delete "${p.name}" from your library? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/products/${p.id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");
      setSelectedIds(prev => { const n = new Set(prev); n.delete(p.id); return n; });
      setProducts(prev => prev.filter(x => x.id !== p.id));
      showToast(`"${p.name}" removed from library`, "success");
    } catch (err) {
      showToast(err.message, "error");
    }
  }, [showToast]);

  // Load library + snippets when sidebar becomes visible (Luke: any module, not just custom)
  useEffect(() => {
    if (!sidebarVisible) return;
    fetch("/api/library").then(r=>r.json()).then(setLibrary).catch(()=>{});
    fetch("/api/snippets").then(r=>r.json()).then(setSnippets).catch(()=>{});
  }, [sidebarVisible]);

  const reloadSnippets = useCallback(() => {
    fetch("/api/snippets").then(r=>r.json()).then(setSnippets).catch(()=>{});
  }, []);

  // Luke: scroll highlighted library card into view
  useEffect(() => {
    if (!libHighlightId) return;
    const el = document.querySelector(`[data-libid="${CSS.escape(libHighlightId)}"]`);
    if (el) el.scrollIntoView({behavior:"smooth",block:"center"});
    const t = setTimeout(()=>setLibHighlightId(null), 2500);
    return () => clearTimeout(t);
  }, [libHighlightId]);

  const allFields = useMemo(() => {
    const seen = new Set(), result = [];
    for (const p of products.filter(p=>selectedIds.has(p.id))) {
      for (const f of p.fields) { if (!seen.has(f.key)) { seen.add(f.key); result.push(f); } }
    }
    return result;
  }, [products, selectedIds]);

  useEffect(() => {
    setFields(prev => {
      const next = {...prev};
      for (const p of products.filter(p=>selectedIds.has(p.id)))
        for (const f of p.fields)
          if (!(f.key in next)) next[f.key] = f.auto&&f.key==="DATE" ? todayISO() : (f.default||"");
      return next;
    });
  }, [selectedIds, products]);

  const addonGroups = useMemo(() => {
    const groups = {};
    for (const p of products.filter(p=>selectedIds.has(p.id))) {
      for (const s of p.sections) {
        if (s.type!=="pricing_table"||!s.rows) continue;
        const label = s.label||"Pricing";
        if (!groups[label]) groups[label]=[];
        const seen = new Set(groups[label].map(r=>r.id));
        for (const row of s.rows) if (row.optional&&row.id&&!seen.has(row.id)) { seen.add(row.id); groups[label].push({id:row.id,module:row.module,price:row.price}); }
      }
    }
    return groups;
  }, [products, selectedIds]);

  const hasAddons = Object.values(addonGroups).some(g=>g.length>0);
  const toggleAddon = useCallback((rowId) => {
    setDisabledAddons(prev=>{const n=new Set(prev);n.has(rowId)?n.delete(rowId):n.add(rowId);return n;});
  }, []);

  // Per-deal payment schedule (see src/shared/payment.js). Declared here because
  // composedSections renders it into the Payment Terms section.
  const [paymentPlan,   setPaymentPlan]   = useState(null);

  // Template name(s) of the current selection — the agreement title's default.
  const defaultAgreementTitle = useMemo(
    () => products.filter(p => selectedIds.has(p.id)).map(p => p.name).join(" / "),
    [products, selectedIds]
  );
  // Follow the selection until the user types their own title.
  useEffect(() => {
    if (!titleTouched) setAgreementTitle(defaultAgreementTitle);
  }, [defaultAgreementTitle, titleTouched]);

  const effectiveTitle = (agreementTitle || defaultAgreementTitle || "").trim();
  const singleModule = products.filter(p => selectedIds.has(p.id)).length === 1;

  const composedSections = useMemo(() => {
    const selectedProducts = products.filter(p=>selectedIds.has(p.id));
    // Tag each section with both module info (for sign page) and source info (for inline editing)
    const tagFromProduct = (p) => p.sections.map((s, idx) => ({ ...s, _module: p.name, _moduleId: p.id, _srcKey: `${p.id}:${idx}`, _srcProductId: p.id, _srcSectionIdx: idx }));
    let sections = selectedProducts.flatMap(tagFromProduct);
    // Apply per-agreement overrides to template sections
    sections = sections.map(s => s._srcKey && sectionOverrides[s._srcKey] !== undefined
      ? { ...s, content: sectionOverrides[s._srcKey] }
      : s);
    // Custom sections are placed further down, once every other insert (hosting,
    // UDS, dynamic bullets, payment milestones) has been added — a "+" clicked
    // under one of those can only find its anchor if the anchor already exists.
    // Hosting inserts — inserted before each product's "Restrictions" heading (if present),
    // otherwise before the signature as a fallback.
    for (const p of selectedProducts) {
      if (!(p.hostingAvailable && hostedProducts.has(p.id) && p.hostingSections)) continue;
      const inserts = [];
      for (let idx = 0; idx < p.hostingSections.length; idx++) {
        const s = p.hostingSections[idx];
        const tagged = { ...s, _module: p.name, _moduleId: p.id, _srcProductId: p.id, _srcSubType: 'hosting', _srcSubIdx: idx, _srcKey: `${p.id}:hosting:${idx}` };
        inserts.push(sectionOverrides[tagged._srcKey] !== undefined ? { ...tagged, content: sectionOverrides[tagged._srcKey] } : tagged);
      }
      if (!inserts.length) continue;
      // Find the first "Restrictions" heading that belongs to this product
      const restrictIdx = sections.findIndex(s => s._srcProductId === p.id && s.type === 'heading' && /restriction/i.test(s.content || ''));
      if (restrictIdx >= 0) {
        sections = [...sections.slice(0, restrictIdx), ...inserts, ...sections.slice(restrictIdx)];
      } else {
        // Fallback: insert before signature
        const sigIdx = sections.reduce((last,s,i) => s.type === "signature" ? i : last, -1);
        sections = sigIdx >= 0 ? [...sections.slice(0,sigIdx), ...inserts, ...sections.slice(sigIdx)] : [...sections, ...inserts];
      }
    }
    // UDS inserts: udsSections is a category-keyed object whose entries get
    // appended to the matching section, just before the next heading. Tag with source.
    const udsByCategoryAcc = {};
    for (const p of selectedProducts) {
      if (p.udsAvailable && udsProducts.has(p.id) && p.udsSections && !Array.isArray(p.udsSections)) {
        for (const [cat, items] of Object.entries(p.udsSections)) {
          if (!udsByCategoryAcc[cat]) udsByCategoryAcc[cat] = [];
          for (let idx = 0; idx < items.length; idx++) {
            const s = items[idx];
            const key = `${p.id}:uds:${cat}:${idx}`;
            const tagged = { ...s, _srcProductId: p.id, _srcSubType: 'uds', _srcSubCategory: cat, _srcSubIdx: idx, _srcKey: key };
            udsByCategoryAcc[cat].push(sectionOverrides[key] !== undefined ? { ...tagged, content: sectionOverrides[key] } : tagged);
          }
        }
      }
    }
    if (Object.keys(udsByCategoryAcc).length > 0) {
      const merged = [];
      let pendingUds = null;
      const flush = () => {
        if (pendingUds) {
          while (pendingUds.length && pendingUds[pendingUds.length-1].type === "spacer") pendingUds.pop();
          merged.push(...pendingUds);
          pendingUds = null;
        }
      };
      for (const s of sections) {
        if (s.type === "heading") {
          flush();
          const cat = classifySectionHeading(s.content);
          pendingUds = udsByCategoryAcc[cat] ? [...udsByCategoryAcc[cat]] : null;
        } else if (s.type === "signature") {
          flush();
        }
        merged.push(s);
      }
      flush();
      sections = merged;
    }
    if (hiddenSrcKeys.size > 0) sections = sections.filter(s => !(s._srcKey && hiddenSrcKeys.has(s._srcKey)));
    // Bundled agreements carry one signature block per product template. The client
    // signs the whole bundle once — keep only the last signature (document end),
    // drop the rest, so the sign page, PDF and docx all show a single signature.
    // Expand "measure_rows" fields into concrete content BEFORE the price-override
    // pass, so section/row indices the rep sees in the preview match the override
    // keys — and so the saved snapshot carries real rows (sign page, PDF and docx
    // need no special handling).
    const measureFieldDef = (key) => {
      for (const p of selectedProducts) {
        const f = (p.fields || []).find(x => x.key === key && x.type === "measure_rows");
        if (f) return f;
      }
      return null;
    };
    const measureEntries = (key) => {
      const def = measureFieldDef(key);
      const opts = def?.options || [];
      return parseMeasureRows(fields[key])
        .map(r => ({ ...r, opt: opts.find(o => o.value === r.type) || null }))
        .filter(r => (r.name || "").trim() || r.opt);
    };
    // Pricing tables hand-edited for this agreement. Applied before the dynamic
    // expansion below and with dynamicRows dropped: the parked rows ARE the
    // expanded ones, so re-expanding would throw the rep's edits away.
    if (Object.keys(tableOverrides).length > 0) {
      sections = sections.map(s => {
        const ov = s.type === "pricing_table" && s._srcKey ? tableOverrides[s._srcKey] : null;
        if (!ov) return s;
        return { ...s, label: ov.label !== undefined ? ov.label : s.label, rows: ov.rows,
                 dynamicRows: undefined, _tableOverridden: true };
      });
    }
    if (sections.some(s => s.dynamicRows || s.type === "dynamic_bullets" || s.type === "dynamic_output_notes")) {
      sections = sections.flatMap(s => {
        // What the client gets for the measures on this addendum. eCQMs come out
        // as QRDA Category I and III; a Quality ID is submitted for MIPS QPP and
        // comes out as JSON, so the one hard-coded QRDA line used to be wrong for
        // every Quality ID sold. Each measure type names its own output note; a
        // mixed addendum prints one line per distinct note, in option order.
        if (s.type === "dynamic_output_notes") {
          const entries = measureEntries(s.source);
          const notes = [];
          for (const e of entries) {
            const note = e.opt?.outputNote || s.default;
            if (note && !notes.includes(note)) notes.push(note);
          }
          if (!notes.length && s.default) notes.push(s.default);
          return notes.map((note, i) => {
            const key = `${s._srcKey || "dynnote"}:${i}`;
            return {
              ...s,
              type: s.bulletType || "arrow_bullet",
              content: sectionOverrides[key] !== undefined ? sectionOverrides[key] : note,
              _srcKey: key,
              _dynamicExpanded: true,
            };
          }).filter(b => !hiddenSrcKeys.has(b._srcKey));
        }
        if (s.type === "dynamic_bullets") {
          const entries = measureEntries(s.source);
          if (!entries.length) {
            return s.empty ? [{ ...s, type: s.bulletType || "arrow_bullet", content: s.empty }] : [];
          }
          // These bullets don't exist until here, so the override and hide passes
          // at the top of composition ran too early to see them — apply theirs
          // now, or a click-to-edit in the preview would silently snap back.
          // _dynamicExpanded marks them as belonging to this agreement only: the
          // template section they carry tags from is the generator, not the text
          // on screen, so nothing here may be written back to it.
          return entries.map((e, i) => {
            const key = `${s._srcKey || "dyn"}:${i}`;
            const wording = measureWording(e);
            return {
              ...s,
              type: s.bulletType || "arrow_bullet",
              content: sectionOverrides[key] !== undefined ? sectionOverrides[key]
                : [(e.name || "").trim() || `Measure ${i + 1}`, wording ? `— ${wording}` : ""].filter(Boolean).join(" "),
              _srcKey: key,
              _dynamicExpanded: true,
            };
          }).filter(b => !hiddenSrcKeys.has(b._srcKey));
        }
        if (s.type === "pricing_table" && s.dynamicRows) {
          const entries = measureEntries(s.dynamicRows);
          const prefix = s.dynamicRowIdPrefix || "dynamic_row";
          const dynRows = entries.map((e, i) => ({
            module: (e.name || "").trim() || `Measure ${i + 1}`,
            items: measureWording(e) ? [measureWording(e) + (e.opt?.note ? ` (${e.opt.note})` : "")] : [],
            price: e.opt ? `$${Number(e.opt.price).toLocaleString("en-US")}` : "",
            initial: false,
            optional: false,
            id: `${prefix}_${i}`,
          }));
          const keep = (s.rows || []).filter(r => r.isTotal);
          return [{ ...s, rows: [...dynRows, ...keep] }];
        }
        return [s];
      });
    }
    // Payment schedule — insert the milestone bullets under the LAST "Payment Terms"
    // heading. Emitted as ordinary bullets so every renderer (sign page, PDF, docx)
    // handles them with no special-casing. Percent-only: the client hasn't chosen
    // their options yet, so a dollar figure here would be a guess.
    const payBullets = paymentPlanBullets(paymentPlan);
    if (payBullets.length) {
      const payIdx = sections.reduce((last, s, i) =>
        (s.type === "heading" && /payment\s*terms/i.test(s.content || "")) ? i : last, -1);
      if (payIdx >= 0) {
        const inserts = payBullets.map((text, i) => ({
          type: i === 0 ? "diamond_bullet" : "arrow_bullet",
          content: text,
          _srcKey: `paymentplan:${i}`,
        }));
        sections = [...sections.slice(0, payIdx + 1), ...inserts, ...sections.slice(payIdx + 1)];
      }
    }
    // Custom sections. Placed last so that every section the preview can show —
    // including hosting, UDS, dynamic measure bullets and payment milestones —
    // is available as an anchor. Placing them earlier meant a "+" clicked under
    // any of those was an orphan by the time it was resolved, and orphans fall
    // back to just before the signature (i.e. under Payment Terms).
    if (customSections.length > 0) {
      const tagged = customSections.map((s, i) => ({...s, _module:s._module||"Additional Terms", _moduleId:s._moduleId||"_additional", _customIdx: i}));
      // Sections inserted from the preview carry _after: the key of the section
      // they should follow, so they stay where they were put. Anything without
      // one (sidebar inserts, older drafts) still lands before the signature.
      const anchored = tagged.filter(s => s._after);
      const floating = tagged.filter(s => !s._after);
      let out = [];
      if (anchored.length) {
        // An inserted section is itself an anchor — a sub-sub bullet added under
        // a sub-bullet you just inserted points at "c:0", not at anything in the
        // template. Place each section's own followers as it goes down, or those
        // chained inserts orphan and drop to the bottom of the agreement.
        const byAnchor = new Map();
        for (const c of anchored) {
          if (!byAnchor.has(c._after)) byAnchor.set(c._after, []);
          byAnchor.get(c._after).push(c);
        }
        const placeWithFollowers = (s) => {
          out.push(s);
          const k = sectionKey(s);
          if (!k || !byAnchor.has(k)) return;
          const followers = byAnchor.get(k);
          byAnchor.delete(k);   // place once, and never loop on a self-referential anchor
          for (const c of followers) placeWithFollowers(c);
        };
        for (const s of sections) placeWithFollowers(s);
      } else {
        out = [...sections];
      }
      // An anchor can vanish (its product was deselected) — keep those sections
      // rather than dropping the user's text on the floor.
      const placed = new Set(out.filter(s => s._customIdx !== undefined).map(s => s._customIdx));
      const tail = [...anchored.filter(c => !placed.has(c._customIdx)), ...floating];
      if (tail.length) {
        const sigIdx = out.reduce((last,s,i)=>s.type==="signature"?i:last, -1);
        out = sigIdx>=0 ? [...out.slice(0,sigIdx),...tail,...out.slice(sigIdx)] : [...out,...tail];
      }
      sections = out;
    }
    const sigIdxs = sections.reduce((acc, s, i) => (s.type === "signature" ? [...acc, i] : acc), []);
    if (sigIdxs.length > 1) {
      const keep = sigIdxs[sigIdxs.length - 1];
      sections = sections.filter((s, i) => s.type !== "signature" || i === keep);
    }
    sections = sections.map((s,si)=>{
      if (s.type!=="pricing_table"||!s.rows) return s;
      const hidden = (row)=>row.optional&&row.id&&disabledAddons.has(row.id);
      const filteredRows = s.rows.filter(row=>!hidden(row))
        .map((row,ri)=>{ const key=`${si}__${ri}`; return priceOverrides[key]!==undefined?{...row,price:priceOverrides[key]}:row; });
      // The same rows with the excluded add-ons put back — what the row editor
      // loads, so hand-editing a table can't quietly delete a row the rep merely
      // unchecked. _composedIdx is this table's half of the priceOverrides key.
      let k = 0;
      const allRows = s.rows.map(row => hidden(row) ? row : filteredRows[k++]);
      return {...s,rows:filteredRows,_rowsAll:allRows,_composedIdx:si};
    });

    // Print the agreement title in place of the template's own title line, but
    // only for a single-module agreement. In a bundle each module keeps its own
    // heading (they start separate pages); there the title names the document
    // as a whole, via _DOC_TITLE.
    if (singleModule && effectiveTitle) {
      const isTitleLine = (s) =>
        (s.type === "title_italic" || s.type === "title") &&
        !/^\{\{\s*(CUSTOMER_NAME|DATE)\s*\}\}$/.test((s.content || "").trim());
      let replaced = false;
      sections = sections.map(s => {
        if (replaced || !isTitleLine(s)) return s;
        replaced = true;
        return { ...s, content: effectiveTitle };
      });
    }
    return sections;
  }, [products,selectedIds,customSections,sectionOverrides,hiddenSrcKeys,priceOverrides,tableOverrides,disabledAddons,hostedProducts,udsProducts,fields,paymentPlan,singleModule,effectiveTitle]);

  const handlePriceChange = useCallback((si,ri,v)=>setPriceOverrides(prev=>({...prev,[`${si}__${ri}`]:v})),[]);

  // Prices proposed by "Draft from notes" arrive as rowIds; priceOverrides wants
  // "<sectionIdx>__<rowIdx>" against the composed document. Convert once the
  // composition reflects the applied selection, then drop the parked copy. A
  // rowId that never shows up (its add-on was excluded, say) is discarded with
  // the rest rather than left pending forever.
  useEffect(() => {
    if (!pendingPrices) return;
    const wanted = new Map(pendingPrices.map(p => [p.rowId, p.price]));
    const resolved = {};
    composedSections.forEach((s, si) => {
      if (s.type !== "pricing_table") return;
      (s.rows || []).forEach((row, ri) => {
        if (row.id && wanted.has(row.id)) resolved[`${si}__${ri}`] = wanted.get(row.id);
      });
    });
    if (Object.keys(resolved).length) setPriceOverrides(prev => ({ ...prev, ...resolved }));
    setPendingPrices(null);
  }, [pendingPrices, composedSections]);

  // Insert a section directly where the "+" was clicked. The new section is
  // anchored to the one above it (_after) and opened for typing straight away —
  // an empty bullet you then have to hunt for would be worse than useless.
  const handleInsertAfter = useCallback((anchorKey, type) => {
    const idx = customSections.length;
    setCustomSections(prev => [...prev, { type, content: "", _after: anchorKey }]);
    if (!["spacer", "hr"].includes(type)) {
      setPreviewEditKey(`c:${idx}`);
      setPreviewEditContent("");
    }
  }, [customSections.length]);

  const templateData = {...fields};
  if (fields.DATE) templateData.DATE = formatDisplayDate(fields.DATE);
  // Drives the PDF/docx running header and the download filename. The generate
  // routes upper-case field keys, and "_DOC_TITLE" is already upper-case, so it
  // arrives intact.
  if (effectiveTitle) templateData._DOC_TITLE = effectiveTitle;
  // The blank "Custom Agreement" prints {{PRODUCT_NAME}} as its title.
  if (effectiveTitle && !fields.PRODUCT_NAME) templateData.PRODUCT_NAME = effectiveTitle;
  const toggleProduct = useCallback((id)=>{
    setSelectedIds(prev=>{
      const n=new Set(prev);
      const wasSelected = n.has(id);
      if (wasSelected) n.delete(id); else n.add(id);
      // Clear edits when the set of selected products changes — overrides are keyed
      // to specific products, so they're stale once the agreement composition changes.
      setSectionOverrides({});
      setCustomSections([]);
      setHiddenSrcKeys(new Set());
      setTableOverrides({});
      setEditPrompt(null);
      return n;
    });
    // Auto-filter the snippet sidebar to the agreement just selected
    setLibSourceFilter(prevFilter => {
      const product = products.find(p => p.id === id);
      if (!product) return prevFilter;
      // If we just selected this product (not deselected), filter to it
      return selectedIds.has(id) ? "all" : product.name;
    });
    setLibSectionFilter("all");
    setLibHighlightId(null);
  },[products, selectedIds]);
  const handleFieldChange = (key,value)=>setFields(prev=>({...prev,[key]:value}));
  const canGenerate = selectedIds.size>0&&fields.CUSTOMER_NAME&&fields.CUSTOMER_NAME.trim().length>0;

  const grouped = useMemo(()=>{
    const map={};
    for(const cat of CATEGORY_ORDER) map[cat]=[];
    for(const p of products){const cat=p.createdByUser?"yours":(PRODUCT_CATEGORY[p.id]||"services");if(map[cat])map[cat].push(p);}
    return map;
  },[products]);

  // handleSectionClickFilter is defined further down; lazily access it via ref
  const filterFnRef = useRef(null);
  const startPreviewEdit = useCallback((key, content, section, category) => {
    setPreviewEditKey(key);
    setPreviewEditContent(content);
    if (sidebarVisible && section && category && category !== "Other" && filterFnRef.current) {
      filterFnRef.current(category, section);
    }
  }, [sidebarVisible]);

  const savePreviewEdit = useCallback((key, content, section) => {
    const originalContent = section?.content || "";
    if (key.startsWith("c:")) {
      const idx = parseInt(key.slice(2), 10);
      setCustomSections(prev => prev.map((s, i) => i === idx ? { ...s, content } : s));
    } else if (key.startsWith("t:")) {
      const srcKey = key.slice(2);
      setSectionOverrides(prev => ({ ...prev, [srcKey]: content }));
    }
    setPreviewEditKey(null);
    setPreviewEditContent("");
    if (content !== originalContent) {
      setEditPrompt({ key, content, section, originalContent });
    }
  }, []);

  const dismissEditPrompt = useCallback(() => setEditPrompt(null), []);

  // Save the just-edited section as a new snippet in the library
  const handleSaveEditAsSnippet = useCallback(async (snippetName) => {
    if (!editPrompt) return;
    const { content, section } = editPrompt;
    const name = (snippetName || "").trim() || truncate(content, 50);
    try {
      await fetch("/api/snippets", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, type: section.type, content, category: "Custom" }) });
      reloadSnippets();
      showToast(`Saved "${name}" to snippet library`);
    } catch { showToast("Could not save to library", "error"); }
    setEditPrompt(null);
  }, [editPrompt, reloadSnippets, showToast]);

  // Push the edit back to the snippet it came from (only for sections inserted from My Snippets)
  const handleUpdateCurrentSnippet = useCallback(async () => {
    if (!editPrompt) return;
    const { content, section } = editPrompt;
    if (!section._snippetId) { showToast("Not from a saved snippet", "error"); setEditPrompt(null); return; }
    try {
      const r = await fetch(`/api/snippets/${section._snippetId}`, { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: section.type, content, category: "Custom" }) });
      if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || "Failed");
      reloadSnippets();
      showToast("Snippet updated");
    } catch (err) { showToast(`Update failed: ${err.message}`, "error"); }
    setEditPrompt(null);
  }, [editPrompt, reloadSnippets, showToast]);

  // Persist this edit back to the source template (products.json)
  const handleUpdateTemplateSection = useCallback(async () => {
    if (!editPrompt) return;
    const { content, section } = editPrompt;
    if (!section._srcProductId) { showToast("Custom sections don't belong to a template", "error"); setEditPrompt(null); return; }
    try {
      const r = await fetch(`/api/products/${encodeURIComponent(section._srcProductId)}/sections/${section._srcSectionIdx}`,
        { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content }) });
      if (!r.ok) throw new Error((await r.json().catch(()=>({}))).error || "Failed");
      // Refresh products and drop the override since the template itself now matches
      const productsRes = await fetch("/api/products").then(x=>x.json());
      setProducts(productsRes);
      setSectionOverrides(prev => { const n = { ...prev }; delete n[section._srcKey]; return n; });
      showToast("Template updated for everyone");
    } catch (err) { showToast(`Update failed: ${err.message}`, "error"); }
    setEditPrompt(null);
  }, [editPrompt, showToast]);

  const agreementEditCount = Object.keys(sectionOverrides).length + customSections.length + Object.keys(tableOverrides).length;
  const hasEdits = agreementEditCount > 0;

  // Click a section in the preview to filter the snippet sidebar and (for
  // body sections) highlight the matching library card.
  const handleSectionClickFilter = useCallback(async (category, section) => {
    setLibTab("library");
    setLibSectionFilter(category);
    setLibSearch("");
    let lib = library;
    try {
      const r = await fetch("/api/library");
      lib = await r.json();
      setLibrary(lib);
    } catch { /* fall back to current state */ }
    if (!section) { setLibSourceFilter("all"); setLibHighlightId(null); return; }
    const norm = (t) => (t || "").trim().replace(/\s+/g, " ");
    const targetType = section.type;
    let match = null;
    if (targetType === "pricing_table") {
      const targetLabel = norm(section.label);
      if (targetLabel) match = lib.find(it => it.type === "pricing_table" && norm(it.label) === targetLabel);
    } else if (targetType === "bank_of_hours") {
      match = lib.find(it => it.type === "bank_of_hours");
    } else {
      const targetContent = norm(section.content);
      match = lib.find(it => it.type === targetType && it.section === category && norm(it.content) === targetContent)
           || lib.find(it => it.type === targetType && norm(it.content) === targetContent);
    }
    setLibHighlightId(match ? match.id : null);
  }, [library]);
  // Expose to startPreviewEdit (which is defined before this) via ref
  useEffect(() => { filterFnRef.current = handleSectionClickFilter; }, [handleSectionClickFilter]);

  // ── Snippet sidebar logic ───────────────────────────────────────────────────

  // Insert a library/snippet item directly. Editing happens in the preview.
  const insertLibraryItem = useCallback((item) => {
    const s = { type: item.type, content: item.content || "" };
    if (item.label) s.label = item.label;
    if (item.rows)  s.rows  = item.rows;
    setCustomSections(prev => [...prev, s]);
    showToast("Inserted — click it in the preview to edit");
  }, [showToast]);

  const insertSnippet = useCallback((snip) => {
    const s = { type: snip.type, content: snip.content || "" };
    if (snip.id) s._snippetId = snip.id;
    setCustomSections(prev => [...prev, s]);
    showToast("Inserted — click it in the preview to edit");
  }, [showToast]);

  const removeCustomSection = useCallback((i)=>setCustomSections(prev=>dropCustomSection(prev,i)),[]);
  const removeSection = useCallback((section) => {
    if (!section) return;
    if (section._customIdx !== undefined) {
      setCustomSections(prev => dropCustomSection(prev, section._customIdx));
    } else if (section._srcKey) {
      setHiddenSrcKeys(prev => { const n = new Set(prev); n.add(section._srcKey); return n; });
      // A section that lives in a template is only hidden for this agreement.
      // Offer to take it out of the template too, so "remove" doesn't quietly
      // mean two different things depending on where the section came from.
      if (section._srcProductId && section._srcSectionIdx != null && !section._srcSubType && !section._dynamicExpanded) {
        setRemovedFromTemplate({
          productId: section._srcProductId,
          productName: section._module || section._srcProductId,
          sectionIdx: section._srcSectionIdx,
          preview: (section.content || section.type || "").slice(0, 60),
        });
      }
    }
    setEditPrompt(null);
  }, []);

  // ── Template section add / remove ───────────────────────────────────────────
  const [removedFromTemplate, setRemovedFromTemplate] = useState(null);

  // Templates the inserted section could be added to (the selected products).
  const addToTemplateTargets = useMemo(
    () => products.filter(p => selectedIds.has(p.id)).map(p => ({ id: p.id, name: p.name })),
    [products, selectedIds]
  );

  const handleAddToTemplate = useCallback(async (productId) => {
    const section = editPrompt?.section;
    if (!section || !productId) return;
    try {
      // Send the position, or the server appends before the signature and the
      // section jumps from where it was written down to under Payment Terms.
      const atIndex = templateInsertIndex(composedSections, section, productId);
      const res = await fetch(`/api/products/${encodeURIComponent(productId)}/sections`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section: { type: section.type, content: section.content || "", label: section.label, indent: section.indent },
          ...(atIndex === null ? {} : { atIndex }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not add to template");
      // Every section below the insert point just moved down one, and the
      // per-agreement keys (edits, hidden sections, insert anchors) are all
      // index-based — renumber them or they'd silently point at the neighbour.
      const at = Number.isInteger(data.atIndex) ? data.atIndex : null;
      const shift = (k) => {
        if (at === null || !k.startsWith(`${productId}:`)) return k;
        const rest = k.slice(productId.length + 1);
        if (!/^\d+$/.test(rest)) return k;   // hosting:/uds: keys carry no top-level index
        const n = Number(rest);
        return n >= at ? `${productId}:${n + 1}` : k;
      };
      setSectionOverrides(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [shift(k), v])));
      setTableOverrides(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [shift(k), v])));
      setHiddenSrcKeys(prev => new Set([...prev].map(shift)));
      // It now lives in the template, so drop the one-off copy to avoid duplicates.
      setCustomSections(prev => {
        let next = prev.map(s => s._after?.startsWith("t:") ? { ...s, _after: `t:${shift(s._after.slice(2))}` } : s);
        if (section._customIdx !== undefined) {
          // Anything inserted under this section (a sub-sub bullet under a
          // sub-bullet) follows it to its new home in the template — handing it
          // this section's own anchor instead would print it above its parent.
          if (at !== null) {
            next = next.map((s, j) => j !== section._customIdx && s._after === `c:${section._customIdx}`
              ? { ...s, _after: `t:${productId}:${at}` } : s);
          }
          next = dropCustomSection(next, section._customIdx);
        }
        return next;
      });
      const fresh = await fetch("/api/products").then(r => r.json());
      setProducts(fresh);
      const name = fresh.find(p => p.id === productId)?.name || productId;
      showToast(`Added to "${name}" — it will appear in future agreements`, "success");
    } catch (err) {
      showToast(err.message, "error");
    }
    setEditPrompt(null);
  }, [editPrompt, composedSections, showToast]);

  const handleRemoveFromTemplate = useCallback(async () => {
    const t = removedFromTemplate;
    if (!t) return;
    try {
      const res = await fetch(`/api/products/${encodeURIComponent(t.productId)}/sections/${t.sectionIdx}`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Could not remove from template");
      const fresh = await fetch("/api/products").then(r => r.json());
      setProducts(fresh);
      // Section indices shifted, so the per-agreement hide/override keys no
      // longer point at the right sections — clear them rather than mis-apply.
      setHiddenSrcKeys(new Set());
      setSectionOverrides({});
      setTableOverrides({});
      showToast(`Removed from "${t.productName}" — future agreements won't include it`, "success");
    } catch (err) {
      showToast(err.message, "error");
    }
    setRemovedFromTemplate(null);
  }, [removedFromTemplate, showToast]);
  const moveCustomSection   = useCallback((i,dir)=>setCustomSections(prev=>{const a=[...prev],t=i+dir;if(t<0||t>=a.length)return prev;[a[i],a[t]]=[a[t],a[i]];return a;}),[]);

  const libSources = useMemo(()=>{const s=new Set();for(const item of library)s.add(item.source);return["all",...Array.from(s)];},[library]);

  const filteredLibrary = useMemo(()=>{
    const q=libSearch.toLowerCase().trim();
    return library.filter(item=>{
      if(libSourceFilter!=="all"&&item.source!==libSourceFilter) return false;
      if(libSectionFilter!=="all"&&item.section!==libSectionFilter) return false;
      if(!q) return true;
      return (item.content||"").toLowerCase().includes(q)||(item.label||"").toLowerCase().includes(q)||(item.source||"").toLowerCase().includes(q)||(item.type||"").toLowerCase().includes(q)||(item.section||"").toLowerCase().includes(q);
    });
  },[library,libSearch,libSourceFilter,libSectionFilter]);

  const filteredSnippets = useMemo(()=>{
    const q=libSearch.toLowerCase().trim();
    if(!q) return snippets;
    return snippets.filter(s=>s.name.toLowerCase().includes(q)||(s.content||"").toLowerCase().includes(q)||(s.category||"").toLowerCase().includes(q));
  },[snippets,libSearch]);

  const handleSaveSnippet = useCallback(async()=>{
    if(!editSnippet||!editSnippet.name?.trim()||!editSnippet.content?.trim()){showToast("Name and content required","error");return;}
    try{
      if(editSnippet.id) await fetch(`/api/snippets/${editSnippet.id}`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(editSnippet)});
      else await fetch("/api/snippets",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(editSnippet)});
      showToast(editSnippet.id?"Snippet updated":"Snippet saved to library"); setEditSnippet(null); reloadSnippets();
    }catch{showToast("Failed to save snippet","error");}
  },[editSnippet,showToast,reloadSnippets]);

  const handleDeleteSnippet = useCallback(async(id)=>{
    try{await fetch(`/api/snippets/${id}`,{method:"DELETE"});showToast("Snippet deleted");reloadSnippets();}
    catch{showToast("Failed to delete","error");}
  },[showToast,reloadSnippets]);

  useEffect(()=>{if(editSnippet&&editNameRef.current)editNameRef.current.focus();},[editSnippet]);

  // ── Submit for approval ─────────────────────────────────────────────────────

  const [submitMode,    setSubmitMode]    = useState(false);
  const [submitDone,    setSubmitDone]    = useState(false);
  const [savedDraftToken, setSavedDraftToken] = useState(null);
  const [savingDraft,   setSavingDraft]   = useState(false);
  const [exporting,     setExporting]     = useState(null); // "pdf" | "docx" | null
  const [buildingLoaded,setBuildingLoaded]= useState(false);
  const [loadedDraftClientToken,setLoadedDraftClientToken]= useState(null);
  const [reviewLoaded,    setReviewLoaded]    = useState(false);
  const [reviewNotFound,  setReviewNotFound]  = useState(false);
  const [reviewDraftProductNames, setReviewDraftProductNames] = useState([]);
  const [reviewApproved,  setReviewApproved]  = useState(false);
  const [reviewAmended,   setReviewAmended]   = useState(false);
  const [reviewAmendOpen, setReviewAmendOpen] = useState(false);
  const [amendNote,       setAmendNote]       = useState("");
  const [amendSubmitting, setAmendSubmitting] = useState(false);
  const [reviewVersionMismatch, setReviewVersionMismatch] = useState(false);
  const [clientEmail,   setClientEmail]   = useState("");
  const [sendingClient, setSendingClient] = useState(false);
  const [hsCompany,     setHsCompany]     = useState(null);
  const [hsDeals,       setHsDeals]       = useState([]);
  const [hsDeal,        setHsDeal]        = useState(null);
  const [hsDealsLoading,setHsDealsLoading]= useState(false);
  const [hsNewDealOpen, setHsNewDealOpen] = useState(false);
  const [hsNewDealName, setHsNewDealName] = useState("");
  const [hsCreatingDeal,setHsCreatingDeal]= useState(false);
  const [hsDealError,   setHsDealError]   = useState("");
  const [sowName,       setSowName]       = useState("");
  // Which HubSpot product(s) this agreement counts against — the deal's
  // "Software" checkbox. Chosen explicitly rather than inferred from the
  // templates: several templates belong to one product line.
  const [hsProducts,    setHsProducts]    = useState([]);
  const toggleHsProduct = useCallback((name)=>{
    setHsProducts(prev=>prev.includes(name)?prev.filter(p=>p!==name):[...prev,name]);
  },[]);
  // Create a deal on the selected company and select it — for companies that
  // have none yet, so an agreement can still be linked to a deal. The SOW name
  // goes over with it: the deal only exists because this agreement is being
  // drafted, so there's no reason to leave the field empty until send.
  const createHsDeal = useCallback(async () => {
    const name = hsNewDealName.trim();
    if (!hsCompany?.id || !name) return;
    setHsCreatingDeal(true); setHsDealError("");
    try {
      const r = await fetch("/api/hubspot/deals", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: hsCompany.id, name, sowName }),
      });
      const d = await r.json();
      if (!r.ok) { setHsDealError(d.error || "Could not create the deal."); return; }
      setHsDeals(prev => [d.deal, ...prev]);
      setHsDeal(d.deal);
      setHsNewDealOpen(false);
      setHsNewDealName("");
    } catch {
      setHsDealError("Could not reach the server.");
    } finally {
      setHsCreatingDeal(false);
    }
  }, [hsCompany, hsNewDealName, sowName]);
  const [hsModalOpen,   setHsModalOpen]   = useState(false);
  // Load the selected company's deals so a SOW name can be written to one of them.
  useEffect(()=>{
    if(!hsCompany?.id){ setHsDeals([]); setHsDeal(null); return; }
    let cancelled=false;
    setHsDealsLoading(true);
    fetch(`/api/hubspot/deals?companyId=${encodeURIComponent(hsCompany.id)}`)
      .then(r=>r.json())
      .then(d=>{ if(cancelled) return;
        const deals=d.deals||[];
        setHsDeals(deals);
        // Keep the saved deal if it's still on this company; auto-pick a lone deal.
        setHsDeal(prev=>{
          if(prev&&deals.some(x=>x.id===prev.id)) return deals.find(x=>x.id===prev.id);
          return deals.length===1?deals[0]:null;
        });
      })
      .catch(()=>{ if(!cancelled) setHsDeals([]); })
      .finally(()=>{ if(!cancelled) setHsDealsLoading(false); });
    return ()=>{cancelled=true;};
  },[hsCompany]);
  const [hsAllCompanies,setHsAllCompanies]= useState([]);
  const [hsConfigured,  setHsConfigured]  = useState(true);
  const [hsFilter,      setHsFilter]      = useState("");
  const [hsLoading,     setHsLoading]     = useState(false);
  const hsFilterRef = useRef(null);

  const handleSaveDraft = async()=>{
    if(!selectedIds.size){showToast("Select at least one module first","error");return;}
    setSavingDraft(true);
    try{
      const body={fields:templateData,rawFields:fields,selectedIds:Array.from(selectedIds),selectedProductNames:products.filter(p=>selectedIds.has(p.id)).map(p=>p.name),sections:composedSections,priceOverrides,sectionOverrides,tableOverrides,hostedProducts:Array.from(hostedProducts),udsProducts:Array.from(udsProducts),customSections,createdBy:currentUser?.name||"Team",hubspotCompanyId:hsCompany?.id||null,hubspotCompanyName:hsCompany?.name||"",hubspotDealId:hsDeal?.id||null,hubspotDealName:hsDeal?.name||"",sowName,hubspotProducts:hsProducts,paymentPlan,agreementTitle:effectiveTitle};
      let res;
      if(savedDraftToken){
        res=await fetch(`/api/draft/${savedDraftToken}`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      } else {
        res=await fetch("/api/draft",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({...body,status:"building"})});
        const data=await res.json();
        if(res.ok) setSavedDraftToken(data.token);
      }
      if(!res.ok) throw new Error((await res.json().catch(()=>({}))).error||"Save failed");
      showToast("Draft saved");
    }catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setSavingDraft(false);}
  };

  // Download the agreement as an unsigned PDF or Word file, straight from what
  // the preview shows. composedSections already has the price overrides and
  // edits baked in (see the memo above), so this is the same input the draft
  // snapshot gets — the file matches the sign page and the executed PDF, minus
  // the signatures. Word is the one to reach for when the agreement still needs
  // a hand edit before it goes out.
  const handleExport = async(format)=>{
    if(!selectedIds.size){showToast("Select at least one module first","error");return;}
    setExporting(format);
    try{
      const res=await fetch(`/api/generate/${format}`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({sections:composedSections,fields:templateData}),
      });
      if(!res.ok) throw new Error((await res.json().catch(()=>({}))).error||"Export failed");
      const blob=await res.blob();
      // a.download wins over the server's Content-Disposition, so build the name
      // from the same helper the server uses or the two would drift.
      const name=agreementFileName({customer:fields.CUSTOMER_NAME,title:effectiveTitle,date:fields.DATE});
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url; a.download=`${name}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      // Revoke on the next tick — Safari cancels the download if the object URL
      // dies in the same frame as the click.
      setTimeout(()=>URL.revokeObjectURL(url),1000);
      showToast(`${format==="pdf"?"PDF":"Word file"} downloaded`);
    }catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setExporting(null);}
  };

  // ── Google Docs ─────────────────────────────────────────────────────────────
  // Same document as the Word download, uploaded to the connected Drive and
  // converted on the way in. Drive has to be connected once (OAuth); until the
  // server has GOOGLE_CLIENT_ID/SECRET the status probe reports unconfigured and
  // the button stays hidden rather than failing on click.
  const [googleState,setGoogleState]=useState(null); // null | {configured, authed}

  useEffect(()=>{
    if(!currentUser) return;
    fetch("/api/google/status")
      .then(r=>r.ok?r.json():null)
      .then(d=>setGoogleState(d?{configured:d.configured!==false,authed:!!d.authed}:null))
      .catch(()=>setGoogleState(null));
  },[currentUser]);

  const connectGoogle = async()=>{
    try{
      const r=await fetch("/api/google/auth");
      const d=await r.json();
      if(!r.ok||!d.url) throw new Error(d.error||"Google Drive is not configured on the server");
      // Popup, then re-probe: the callback closes its own tab, so poll for the
      // token landing rather than asking the user to hit the button twice.
      const win=window.open(d.url,"google-auth","width=520,height=640");
      const started=Date.now();
      const poll=setInterval(async()=>{
        if(Date.now()-started>180000||(win&&win.closed)){
          clearInterval(poll);
          const s=await fetch("/api/google/status").then(x=>x.json()).catch(()=>null);
          if(s?.authed){setGoogleState({configured:true,authed:true});showToast("Google Drive connected");}
          return;
        }
        const s=await fetch("/api/google/status").then(x=>x.json()).catch(()=>null);
        if(s?.authed){
          clearInterval(poll);
          setGoogleState({configured:true,authed:true});
          showToast("Google Drive connected");
        }
      },2000);
    }catch(err){showToast(`Error: ${err.message}`,"error");}
  };

  const handleExportGoogleDoc = async()=>{
    if(!selectedIds.size){showToast("Select at least one module first","error");return;}
    if(!googleState?.authed){connectGoogle();return;}
    setExporting("gdocs");
    try{
      const res=await fetch("/api/generate/gdocs",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({sections:composedSections,fields:templateData}),
      });
      const d=await res.json().catch(()=>({}));
      if(res.status===401){
        setGoogleState({configured:true,authed:false});
        throw new Error(d.error||"Google Drive needs to be reconnected");
      }
      if(!res.ok||!d.url) throw new Error(d.error||"Export failed");
      window.open(d.url,"_blank","noopener");
      showToast(d.shared?"Opened in Google Docs":"Created in your Google Drive — share it from there");
    }catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setExporting(null);}
  };

  const handleSubmitForApproval = async()=>{
    if(!canGenerate) return; setSubmitMode(true);
    try{
      const body={fields:templateData,rawFields:fields,selectedIds:Array.from(selectedIds),selectedProductNames:products.filter(p=>selectedIds.has(p.id)).map(p=>p.name),sections:composedSections,priceOverrides,sectionOverrides,tableOverrides,hostedProducts:Array.from(hostedProducts),udsProducts:Array.from(udsProducts),customSections,createdBy:currentUser?.name||"Team",hubspotCompanyId:hsCompany?.id||null,hubspotCompanyName:hsCompany?.name||"",hubspotDealId:hsDeal?.id||null,hubspotDealName:hsDeal?.name||"",sowName,hubspotProducts:hsProducts,paymentPlan,agreementTitle:effectiveTitle};
      let res;
      if(savedDraftToken){
        res=await fetch(`/api/draft/${savedDraftToken}`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({...body,status:"pending_review"})});
      } else {
        res=await fetch("/api/draft",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});
      }
      if(!res.ok) throw new Error((await res.json().catch(()=>({}))).error||"Submission failed");
      setSubmitDone(true); showToast("Agreement submitted for approval!");
    }catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setSubmitMode(false);}
  };

  function resetBuilder() {
    setSelectedIds(new Set());
    setFields({});
    setCustomSections([]);
    setSectionOverrides({});
    setHostedProducts(new Set());
    setUdsProducts(new Set());
    setPriceOverrides({});
    setTableOverrides({});
    setDisabledAddons(new Set());
    setSubmitMode(false);
    setSubmitDone(false);
    setSavedDraftToken(null);
    setSavingDraft(false);
    setBuildingLoaded(false);
    setReviewLoaded(false);
    setReviewDraftProductNames([]);
    setReviewApproved(false);
    setReviewAmended(false);
    setReviewAmendOpen(false);
    setAmendNote("");
    setReviewVersionMismatch(false);
    setClientEmail("");
    setSendingClient(false);
    setSendDone(false);
    setHsCompany(null);
  }

  // Load a building draft when opened via /draft/:token
  useEffect(()=>{
    if(!savedDraftToken||buildingLoaded||reviewMode||sendMode||!authChecked||!currentUser) return;
    fetch(`/api/draft/${savedDraftToken}`).then(r=>r.json()).then(d=>{
      if(d.status!=='building') return;
      if(d.rawFields)    setFields(d.rawFields);
      else if(d.fields)  setFields(d.fields);
      if(d.selectedIds)    setSelectedIds(new Set(d.selectedIds));
      if(d.priceOverrides) setPriceOverrides(d.priceOverrides);
      // Reopening your own draft has to bring your edits back with it — the
      // saved `sections` are a snapshot for the reviewer, but the builder
      // recomposes from the templates and would otherwise show them undone.
      if(d.sectionOverrides) setSectionOverrides(d.sectionOverrides);
      if(d.tableOverrides)   setTableOverrides(d.tableOverrides);
      if(d.hostedProducts) setHostedProducts(new Set(d.hostedProducts));
      if(d.customSections) setCustomSections(d.customSections);
      if(d.hubspotCompanyId) { setHsCompany({id:d.hubspotCompanyId,name:d.hubspotCompanyName||""}); }
      if(d.hubspotDealId) setHsDeal({id:d.hubspotDealId,name:d.hubspotDealName||""});
      if(d.sowName) setSowName(d.sowName);
      if(d.hubspotProducts) setHsProducts(d.hubspotProducts);
      if(d.agreementTitle){ setAgreementTitle(d.agreementTitle); setTitleTouched(true); }
      if(d.paymentPlan) setPaymentPlan(d.paymentPlan);
      if(d.clientToken) setLoadedDraftClientToken(d.clientToken);
      setBuildingLoaded(true);
    }).catch(()=>showToast("Could not load saved draft","error"));
  },[savedDraftToken,buildingLoaded,reviewMode,sendMode,authChecked,currentUser,showToast]);

  useEffect(()=>{
    if(!reviewMode||!reviewToken||reviewLoaded||!authChecked||!currentUser) return;
    fetch(`/api/draft/${reviewToken}`).then(r=>{
      if(r.status===404){setReviewNotFound(true);setReviewLoaded(true);return null;}
      return r.json();
    }).then(draft=>{
      if(!draft) return;
      if(draft.error){setReviewNotFound(true);setReviewLoaded(true);return;}
      if(draft.rawFields)      setFields(draft.rawFields);
      else if(draft.fields)    setFields(draft.fields);
      if(draft.selectedIds)    setSelectedIds(new Set(draft.selectedIds));
      if(draft.selectedProductNames) setReviewDraftProductNames(draft.selectedProductNames);
      if(draft.priceOverrides) setPriceOverrides(draft.priceOverrides);
      if(draft.sectionOverrides) setSectionOverrides(draft.sectionOverrides);
      if(draft.tableOverrides)   setTableOverrides(draft.tableOverrides);
      if(draft.hostedProducts) setHostedProducts(new Set(draft.hostedProducts));
      if(draft.udsProducts)    setUdsProducts(new Set(draft.udsProducts));
      if(draft.customSections) setCustomSections(draft.customSections);
      if(draft.hubspotCompanyId) setHsCompany({id:draft.hubspotCompanyId,name:draft.hubspotCompanyName||""});
      if(draft.hubspotDealId) setHsDeal({id:draft.hubspotDealId,name:draft.hubspotDealName||""});
      if(draft.sowName) setSowName(draft.sowName);
      if(draft.hubspotProducts) setHsProducts(draft.hubspotProducts);
      if(draft.agreementTitle){ setAgreementTitle(draft.agreementTitle); setTitleTouched(true); }
      if(draft.paymentPlan) setPaymentPlan(draft.paymentPlan);
      const urlV = parseInt(new URLSearchParams(window.location.search).get("v")) || 1;
      if ((draft.reviewVersion || 1) > urlV) setReviewVersionMismatch(true);
      setReviewLoaded(true);
    }).catch(()=>showToast("Could not load draft","error"));
  },[reviewMode,reviewToken,reviewLoaded,authChecked,currentUser,showToast]);

  // Fallback: reconstruct selectedIds from product names when draft lacks selectedIds
  useEffect(()=>{
    if(!reviewLoaded||!reviewMode||!products.length||selectedIds.size>0||!reviewDraftProductNames.length) return;
    const nameSet=new Set(reviewDraftProductNames);
    const ids=products.filter(p=>nameSet.has(p.name)).map(p=>p.id);
    if(ids.length) setSelectedIds(new Set(ids));
  },[reviewLoaded,reviewMode,products,reviewDraftProductNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = async()=>{
    const bossId=sessionStorage.getItem("bossId")||"boss1";
    try{const res=await fetch(`/api/draft/${reviewToken}/approve`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({bossId,bossName:currentUser?.name||bossId})});const d=await res.json();setReviewApproved(true);showToast(d.allApproved?"Both approved — team notified!":"Your approval recorded.");}
    catch{showToast("Approval failed","error");}
  };

  const handleAmend = async()=>{
    const bossId=sessionStorage.getItem("bossId")||"boss1";
    setAmendSubmitting(true);
    try{
      const updatedContent={fields:templateData,rawFields:fields,sections:composedSections,customSections,priceOverrides,selectedIds:Array.from(selectedIds),selectedProductNames:products.filter(p=>selectedIds.has(p.id)).map(p=>p.name)};
      const res=await fetch(`/api/draft/${reviewToken}/amend`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({bossId,bossName:currentUser?.name||bossId,note:amendNote,updatedContent})});
      if(!res.ok) throw new Error((await res.json().catch(()=>({}))).error||"Failed");
      setReviewAmendOpen(false);
      setReviewAmended(true);
      showToast("Amendments submitted — team and both reviewers notified.");
    }catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setAmendSubmitting(false);}
  };

  const hsSearchTimer = useRef(null);
  const openHsModal = ()=>{
    setHsFilter(""); setHsAllCompanies([]); setHsModalOpen(true);
    setTimeout(()=>hsFilterRef.current?.focus(),50);
  };
  const handleHsFilterChange = (val)=>{
    setHsFilter(val);
    clearTimeout(hsSearchTimer.current);
    if(val.trim().length < 2){ setHsAllCompanies([]); setHsLoading(false); return; }
    setHsLoading(true);
    hsSearchTimer.current = setTimeout(async()=>{
      try{const res=await fetch(`/api/hubspot/search?q=${encodeURIComponent(val.trim())}`);const d=await res.json();setHsAllCompanies(d.results||[]);}
      catch(e){console.error('[hubspot]',e);}
      finally{setHsLoading(false);}
    }, 300);
  };

  useEffect(()=>{
    if(!sendMode||!sendToken) return;
    fetch(`/api/draft/${sendToken}`).then(r=>r.json()).then(d=>setSendDraft(d)).catch(()=>{});
  },[sendMode,sendToken]);

  const handleSendToClient = async()=>{
    if(!clientEmail.trim()){showToast("Enter a client email","error");return;} setSendingClient(true);
    try{const res=await fetch(`/api/draft/${sendToken}/send-client`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({clientEmail,override:overrideSend||undefined,hubspotDealId:hsDeal?.id||null,hubspotDealName:hsDeal?.name||"",sowName,hubspotProducts:hsProducts,paymentPlan})});if(!res.ok)throw new Error((await res.json().catch(()=>({}))).error||"Failed");setSendDone(true);showToast("Sign link sent to client!");}
    catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setSendingClient(false);}
  };

  const handleResendJeff = async()=>{
    setResendingJeff(true);
    try{
      const res=await fetch(`/api/draft/${sendToken}/resend-jeff`,{method:"POST",headers:{"Content-Type":"application/json"}});
      const data=await res.json().catch(()=>({}));
      if(!res.ok) throw new Error(data.error||"Failed");
      if(data.emailWarning){
        showToast("Could not deliver email — check Jeff's address in settings","error");
      } else {
        showToast("Countersign email resent to Jeff!");
        setSendDraft(d=>({...d, jeffEmailFailed: false}));
      }
    }
    catch(err){showToast(`Error: ${err.message}`,"error");}
    finally{setResendingJeff(false);}
  };

  // ── Render guards ───────────────────────────────────────────────────────────

  if (!authChecked) return (
    <div style={{minHeight:"100vh",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{color:"#888",fontSize:14,fontFamily:"Arial,sans-serif"}}>Loading…</div>
    </div>
  );
  if (signMode && signClientToken)       return <SignPage clientToken={signClientToken} />;
  if (countersignMode && jeffToken)      return <CountersignPage jeffToken={jeffToken} />;
  if (commentsMode && commentsToken && currentUser) return <CommentsReviewPage token={commentsToken} user={currentUser} onBack={() => window.location.href='/'} />;
  if (!currentUser) return <LoginPage onLogin={user=>{
    setCurrentUser(user);
    // After login, redirect to home unless we're on a review or send URL
    // (draft URLs should not auto-open the builder — user can open from home)
    if (!reviewMode && !sendMode && window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }} />;

  if (screen==="test") {
    return <TestPanel onBack={()=>setScreen("home")} />;
  }

  if (screen==="changelog") {
    return <ChangelogPage onBack={()=>setScreen("home")} />;
  }

  if (sendMode) {
    // Show spinner while we fetch the draft status
    if (!sendDraft) return (
      <div style={{minHeight:"100vh",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{color:"#888",fontSize:14,fontFamily:"Arial,sans-serif"}}>Loading…</div>
      </div>
    );

    // Client has already signed — show Jeff countersign status screen
    if (sendDraft?.status === 'client_signed') {
      const signedAt = sendDraft.signedAt ? new Date(sendDraft.signedAt).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}) : null;
      const countersignUrl = `${window.location.origin}/countersign/${sendDraft.jeffToken}`;
      return (
        <div style={{minHeight:"100vh",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Arial,sans-serif"}}>
          <div style={{background:"#fff",borderRadius:12,padding:"40px 36px",width:"100%",maxWidth:480,boxShadow:"0 2px 16px rgba(0,0,0,0.08)"}}>
            <img src="/logo.png" alt="DHIT" style={{height:44,marginBottom:20}} onError={e=>{e.target.style.display="none";}}/>
            <div style={{fontSize:36,marginBottom:12}}>✍</div>
            <div style={{fontSize:18,fontWeight:700,color:"#1a3a6e",marginBottom:6}}>Awaiting Jeff's Counter-Signature</div>
            {signedAt && <p style={{color:"#666",fontSize:13,marginBottom:16}}>Signed by {sendDraft.signerName || 'client'} on {signedAt}. Jeff has been notified by email.</p>}
            {sendDraft.jeffEmailFailed && (
              <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:13,color:"#c62828",lineHeight:1.5}}>
                <strong>Email warning:</strong> Jeff's countersign notification may not have been delivered. Use the button below to resend, or share the link manually.
              </div>
            )}
            <div style={{background:"#f4f5f7",borderRadius:8,padding:"10px 14px",marginBottom:20,fontSize:12,color:"#555",wordBreak:"break-all",lineHeight:1.6}}>
              <div style={{fontWeight:600,color:"#1a3a6e",marginBottom:4,fontSize:11,textTransform:"uppercase",letterSpacing:"0.04em"}}>Jeff's Countersign Link</div>
              {countersignUrl}
            </div>
            <button onClick={handleResendJeff} disabled={resendingJeff} style={{width:"100%",background:"#1a3a6e",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:12,opacity:resendingJeff?0.6:1}}>
              {resendingJeff?"Sending…":"Resend Email to Jeff →"}
            </button>
            <button onClick={()=>window.location.href='/'} style={{width:"100%",background:"#fff",color:"#666",border:"1px solid #ddd",borderRadius:8,padding:10,fontSize:14,cursor:"pointer"}}>← Back to Home</button>
          </div>
        </div>
      );
    }

    return (
      <div style={{minHeight:"100vh",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Arial,sans-serif"}}>
        <div style={{background:"#fff",borderRadius:12,padding:"40px 36px",width:"100%",maxWidth:460,boxShadow:"0 2px 16px rgba(0,0,0,0.08)"}}>
          <img src="/logo.png" alt="DHIT" style={{height:44,marginBottom:20}} onError={e=>{e.target.style.display="none";}}/>
          {sendDone ? (
            <><div style={{fontSize:36,marginBottom:12}}>✓</div><div style={{fontSize:18,fontWeight:700,color:"#1a3a6e",marginBottom:8}}>Sign link sent!</div><p style={{color:"#666",fontSize:14}}>The client has been emailed their sign link and access code.</p></>
          ):(
            <>
              <div style={{fontSize:18,fontWeight:700,color:"#1a3a6e",marginBottom:8}}>Send to Client</div>
              {overrideSend ? (
                <div style={{background:"#fff8e1",border:"1px solid #fcd34d",borderRadius:8,padding:"10px 14px",marginBottom:20,fontSize:13,color:"#92400e",lineHeight:1.5}}>
                  <strong>Override:</strong> Only one boss has approved. You are sending this agreement without full dual approval.
                </div>
              ) : (
                <p style={{color:"#666",fontSize:13,marginBottom:20}}>Both bosses have approved. Enter the client's email to send their sign link.</p>
              )}
              <input type="email" placeholder="client@company.com" value={clientEmail} onChange={e=>setClientEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSendToClient()} style={{width:"100%",padding:"10px 12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,boxSizing:"border-box",marginBottom:12}}/>
              <button onClick={handleSendToClient} disabled={sendingClient||!clientEmail} style={{width:"100%",background:"#1a3a6e",color:"#fff",border:"none",borderRadius:8,padding:11,fontSize:15,fontWeight:600,cursor:"pointer",opacity:!clientEmail||sendingClient?0.5:1}}>{sendingClient?"Sending…":"Send Sign Link →"}</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Home page is the default for authenticated users not in a special mode
  if (!reviewMode && screen !== "builder") {
    return (
      <HomePage user={currentUser} onCreateNew={()=>{ resetBuilder(); setScreen("builder"); }} onTestPanel={()=>setScreen("test")} onChangelog={()=>setScreen("changelog")}
        onOpenDraft={draft=>{
          if(draft.status==="building")             window.location.href=`/draft/${draft.token}`;
          else if(draft.status==="pending_review")  window.location.href=`/review/${draft.token}`;
          else if(draft.status==="client_commented") window.location.href=`/comments/${draft.token}`;
          else                                      window.location.href=`/send/${draft.token}`;
        }}
        onOverrideSend={draft=>{ window.location.href=`/send/${draft.token}?override=1`; }}
        onViewComments={draft=>{ window.location.href=`/comments/${draft.token}`; }}
      />
    );
  }

  if (reviewMode && reviewNotFound) return (
    <div style={{minHeight:"100vh",background:"#f4f5f7",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Arial,sans-serif"}}>
      <div style={{background:"#fff",borderRadius:12,padding:"40px 36px",maxWidth:460,textAlign:"center",boxShadow:"0 2px 16px rgba(0,0,0,0.08)"}}>
        <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
        <div style={{fontSize:18,fontWeight:700,color:"#1a3a6e",marginBottom:8}}>Agreement Not Found</div>
        <p style={{color:"#666",fontSize:14,lineHeight:1.6,marginBottom:20}}>
          This review link is no longer valid. The agreement may have been updated or the server was redeployed.
          Please ask the team to resubmit the agreement.
        </p>
        <button onClick={()=>window.location.href='/'} style={{background:"#1a3a6e",color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontSize:14,fontWeight:600,cursor:"pointer"}}>← Go Home</button>
      </div>
    </div>
  );

  // ── Main builder ────────────────────────────────────────────────────────────

  return (
    <div className="app">
      <header style={{
        background:"linear-gradient(160deg,#12285a 0%,#1a3a6e 55%,#1e4481 100%)",
        padding:"0 32px",display:"flex",alignItems:"center",justifyContent:"space-between",
        height:68,flexShrink:0,boxShadow:"0 3px 16px rgba(0,0,0,0.22)",position:"relative",zIndex:10,
      }}>
        {/* Bottom accent stripe */}
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:3,background:"linear-gradient(90deg,#2563eb 0%,#38bdf8 40%,#1D9E75 100%)",opacity:0.7}}/>

        {/* Left: logo + divider + title + home link */}
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <img src="/logo.png" alt="DHIT" style={{height:38,objectFit:"contain"}} onError={e=>{e.target.style.display="none";}}/>
          <div style={{width:1,height:30,background:"rgba(255,255,255,0.18)",flexShrink:0}}/>
          <div>
            <div style={{color:"#fff",fontWeight:700,fontSize:16,letterSpacing:"-0.2px",lineHeight:1.2}}>Agreement Builder</div>
            <div style={{color:"rgba(255,255,255,0.45)",fontSize:11,letterSpacing:"0.03em",marginTop:2}}>Dynamic Health IT, Inc.</div>
          </div>
          {!sendMode&&(
            <button onClick={()=>{if(reviewMode){window.location.href='/';}else{setScreen("home");}}}
              style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.18)",cursor:"pointer",fontSize:13,color:"rgba(255,255,255,0.75)",padding:"6px 14px",borderRadius:7,marginLeft:8}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.15)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.08)";}}>
              ← Home
            </button>
          )}
        </div>

        {/* Right: review badge + date + user + sign out */}
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {reviewMode&&(
            <span style={{background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",color:"rgba(255,255,255,0.85)",fontSize:12,fontWeight:600,padding:"5px 12px",borderRadius:20,letterSpacing:"0.02em"}}>
              Review mode
            </span>
          )}
          <span style={{color:"rgba(255,255,255,0.45)",fontSize:12}}>{todayFormatted()}</span>
          <div style={{width:1,height:24,background:"rgba(255,255,255,0.16)"}}/>
          <div style={{display:"flex",alignItems:"center",gap:9}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:"rgba(255,255,255,0.14)",border:"1.5px solid rgba(255,255,255,0.28)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:13,fontWeight:700,flexShrink:0}}>
              {currentUser?.name?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()||"?"}
            </div>
            <span style={{color:"rgba(255,255,255,0.82)",fontSize:13,fontWeight:500,whiteSpace:"nowrap"}}>{currentUser?.name}</span>
          </div>
          <button onClick={async()=>{await fetch("/api/auth/logout",{method:"POST"});window.location.reload();}}
            style={{background:"transparent",border:"1px solid rgba(255,255,255,0.22)",color:"rgba(255,255,255,0.72)",padding:"7px 14px",borderRadius:7,fontSize:12,cursor:"pointer"}}
            onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.color="#fff";}}
            onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="rgba(255,255,255,0.72)";}}>
            Sign out
          </button>
        </div>
      </header>

      <div className="app-body">

        {/* Left: Info Panel */}
        <aside className="info-panel" style={{width: infoPanelWidth}}>
          <div className="info-scroll">
            <div className="info-section-header">Customer Information</div>
            <div className="field-group" style={{marginBottom:12}}>
              <label className="field-label">HubSpot Company <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>(optional)</span></label>
              {hsCompany?(
                <div style={{display:"flex",alignItems:"center",gap:8,background:"#f0f4ff",border:"1px solid #c5d3f5",borderRadius:6,padding:"7px 10px"}}>
                  <span style={{flex:1,fontSize:13,fontWeight:600,color:"#1a3a6e"}}>{hsCompany.name}</span>
                  <button onClick={()=>setHsCompany(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#aaa",fontSize:18,lineHeight:1,padding:0}}>×</button>
                </div>
              ):(
                <button onClick={openHsModal} style={{width:"100%",background:"#1a3a6e",color:"#fff",border:"none",borderRadius:6,padding:"8px 12px",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"center"}}>
                  + Select Company
                </button>
              )}
            </div>

            {/* Deal + SOW name — written back to HubSpot when the agreement is sent */}
            {hsCompany&&(
              <div className="field-group" style={{marginBottom:12}}>
                <label className="field-label">HubSpot Deal <span style={{fontWeight:400,color:"#aaa",fontSize:11}}>(optional)</span></label>
                {hsDealsLoading?(
                  <div style={{fontSize:12,color:"#999",padding:"6px 0"}}>Loading deals…</div>
                ):(
                  <>
                    {hsDeals.length>0&&(
                      <select className="input" value={hsDeal?.id||""}
                        onChange={e=>{const d=hsDeals.find(x=>x.id===e.target.value)||null;setHsDeal(d);}}>
                        <option value="">— none —</option>
                        {hsDeals.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    )}
                    {hsDeals.length===0&&!hsNewDealOpen&&(
                      <div style={{fontSize:11,color:"#999",padding:"4px 0"}}>No deals found on this company.</div>
                    )}
                    {hsNewDealOpen?(
                      <div style={{marginTop:6}}>
                        <input type="text" className="input" autoFocus
                          placeholder="Deal name" value={hsNewDealName}
                          onChange={e=>setHsNewDealName(e.target.value)}
                          onKeyDown={e=>{if(e.key==="Enter")createHsDeal();if(e.key==="Escape")setHsNewDealOpen(false);}}/>
                        {hsDealError&&<div style={{fontSize:11,color:"#c0392b",marginTop:4}}>{hsDealError}</div>}
                        <div style={{display:"flex",gap:6,marginTop:6}}>
                          <button type="button" onClick={createHsDeal} disabled={hsCreatingDeal||!hsNewDealName.trim()}
                            style={{background:"#1a3a6e",color:"#fff",border:"none",borderRadius:4,padding:"5px 12px",fontSize:11,fontWeight:600,cursor:hsCreatingDeal?"default":"pointer",opacity:(hsCreatingDeal||!hsNewDealName.trim())?0.6:1}}>
                            {hsCreatingDeal?"Creating…":"Create deal"}
                          </button>
                          <button type="button" onClick={()=>{setHsNewDealOpen(false);setHsDealError("");}}
                            style={{background:"none",border:"1px solid #ddd",borderRadius:4,padding:"5px 12px",fontSize:11,cursor:"pointer",color:"#666"}}>Cancel</button>
                        </div>
                      </div>
                    ):(
                      <button type="button"
                        onClick={()=>{
                          setHsDealError("");
                          const prods=products.filter(p=>selectedIds.has(p.id)).map(p=>p.name).join(", ");
                          const cust=fields.CUSTOMER_NAME||hsCompany?.name||"";
                          setHsNewDealName([cust,prods].filter(Boolean).join(" — "));
                          setHsNewDealOpen(true);
                        }}
                        style={{marginTop:6,background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:4,color:"#1a3a6e",cursor:"pointer",fontSize:11,fontWeight:600,padding:"4px 10px"}}>
                        + New deal
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {hsCompany&&hsDeal&&(
              <>
                <div className="field-group" style={{marginBottom:12}}>
                  <label className="field-label">SOW Name (Concierge)</label>
                  <input type="text" className="input" placeholder="e.g. TestEHR — CQMsolution Add Measures"
                    value={sowName} onChange={e=>setSowName(e.target.value)}/>
                  <div style={{fontSize:11,color:"#999",marginTop:4}}>
                    Written to the deal in HubSpot when the agreement is sent.
                  </div>
                </div>
                <div className="field-group" style={{marginBottom:12}}>
                  <label className="field-label">Product</label>
                  <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                    {HS_SOFTWARE_OPTIONS.map(name=>{
                      const on=hsProducts.includes(name);
                      return (
                        <button key={name} type="button" onClick={()=>toggleHsProduct(name)}
                          aria-pressed={on}
                          style={{border:`1px solid ${on?"#1a3a6e":"#ddd"}`,background:on?"#eef2ff":"#fff",
                                  color:on?"#1a3a6e":"#666",borderRadius:14,padding:"4px 11px",fontSize:11,
                                  fontWeight:on?600:400,cursor:"pointer"}}>
                          {on?"✓ ":""}{name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{fontSize:11,color:"#999",marginTop:4}}>
                    Tags the deal's Software field so deals can be filtered by product. Pick as many as apply.
                  </div>
                </div>
              </>
            )}

            {/* Payment schedule — per-deal, written into Payment Terms and the signed order */}
            <div className="field-group" style={{marginBottom:12}}>
              <label className="field-label">Payment Schedule</label>
              <select className="input"
                value={paymentPlan?.type==="milestones"?(paymentPlan.preset||"custom"):"due_on_receipt"}
                onChange={e=>{
                  const v=e.target.value;
                  if(v==="due_on_receipt"){setPaymentPlan(null);return;}
                  const preset=PAYMENT_PRESETS[v];
                  setPaymentPlan({type:"milestones",preset:v,milestones:preset?preset.milestones.map(m=>({...m})):[{label:"Upon signature of this document",percent:50},{label:"Upon completion",percent:50}]});
                }}>
                <option value="due_on_receipt">Due on receipt (default)</option>
                {Object.entries(PAYMENT_PRESETS).filter(([k])=>k!=="due_on_receipt").map(([k,v])=>(
                  <option key={k} value={k}>{v.label}</option>
                ))}
                <option value="custom">Custom milestones…</option>
              </select>

              {paymentPlan?.type==="milestones"&&(
                <div style={{marginTop:8}}>
                  {paymentPlan.milestones.map((m,i)=>(
                    <div key={i} style={{display:"flex",gap:4,alignItems:"center",marginBottom:5}}>
                      <input type="text" className="input" style={{flex:"1 1 auto",minWidth:0}}
                        placeholder="e.g. Upon go-live" value={m.label}
                        onChange={e=>setPaymentPlan(p=>({...p,preset:"custom",milestones:p.milestones.map((x,j)=>j===i?{...x,label:e.target.value}:x)}))}/>
                      <input type="number" className="input" style={{flex:"0 0 64px"}} min="0" max="100"
                        value={m.percent}
                        onChange={e=>setPaymentPlan(p=>({...p,preset:"custom",milestones:p.milestones.map((x,j)=>j===i?{...x,percent:Number(e.target.value)}:x)}))}/>
                      <span style={{fontSize:11,color:"#888"}}>%</span>
                      <button type="button" title="Remove milestone"
                        onClick={()=>setPaymentPlan(p=>{const next=p.milestones.filter((_,j)=>j!==i);return next.length?{...p,preset:"custom",milestones:next}:null;})}
                        style={{background:"none",border:"none",color:"#c0392b",cursor:"pointer",fontSize:14,lineHeight:1,padding:"2px 4px"}}>✕</button>
                    </div>
                  ))}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:4}}>
                    <button type="button"
                      onClick={()=>setPaymentPlan(p=>({...p,preset:"custom",milestones:[...p.milestones,{label:"",percent:0}]}))}
                      style={{background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:4,color:"#1a3a6e",cursor:"pointer",fontSize:11,fontWeight:600,padding:"4px 10px"}}>+ Add milestone</button>
                    {(()=>{const t=paymentPlan.milestones.reduce((a,m)=>a+(Number(m.percent)||0),0);
                      return <span style={{fontSize:11,fontWeight:600,color:t===100?"#1a6e3c":"#c0392b"}}>{t}%{t===100?" ✓":" — must total 100%"}</span>;})()}
                  </div>
                </div>
              )}
              <div style={{fontSize:11,color:"#999",marginTop:4}}>
                Percentages appear in Payment Terms; dollar amounts are computed on the signed total.
              </div>
            </div>
            {hsModalOpen&&(
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>setHsModalOpen(false)}>
                <div style={{background:"#fff",borderRadius:12,width:480,maxWidth:"95vw",maxHeight:"70vh",display:"flex",flexDirection:"column",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
                  <div style={{display:"flex",alignItems:"center",padding:"16px 20px",borderBottom:"1px solid #eee"}}>
                    <span style={{flex:1,fontWeight:700,fontSize:15,color:"#1a3a6e"}}>Select HubSpot Company</span>
                    <button onClick={()=>setHsModalOpen(false)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"#aaa",lineHeight:1,padding:0}}>×</button>
                  </div>
                  <div style={{padding:"12px 20px",borderBottom:"1px solid #eee"}}>
                    <input ref={hsFilterRef} placeholder="Type to search companies…" value={hsFilter} onChange={e=>handleHsFilterChange(e.target.value)} style={{width:"100%",padding:"9px 12px",border:"1px solid #ddd",borderRadius:6,fontSize:14,boxSizing:"border-box"}}/>
                  </div>
                  <div style={{overflowY:"auto",flex:1}}>
                    {!hsConfigured?(
                      <div style={{padding:24,textAlign:"center",color:"#999",fontSize:13}}>HubSpot not configured in this environment</div>
                    ):hsLoading?(
                      <div style={{padding:24,textAlign:"center",color:"#999",fontSize:13}}>Searching…</div>
                    ):hsFilter.trim().length < 2?(
                      <div style={{padding:24,textAlign:"center",color:"#999",fontSize:13}}>Type at least 2 characters to search</div>
                    ):hsAllCompanies.length===0?(
                      <div style={{padding:24,textAlign:"center",color:"#999",fontSize:13}}>No companies found</div>
                    ):(
                      hsAllCompanies.map(c=>(
                        <div key={c.id} onClick={()=>{setHsCompany(c);setHsModalOpen(false);handleFieldChange('CUSTOMER_NAME',c.name);}} style={{padding:"10px 20px",cursor:"pointer",borderBottom:"1px solid #f4f4f4",display:"flex",flexDirection:"column",gap:2}} onMouseEnter={e=>e.currentTarget.style.background="#f4f5f7"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <span style={{fontWeight:600,fontSize:13,color:"#1a3a6e"}}>{c.name}</span>
                          {(c.city||c.domain)&&<span style={{fontSize:11,color:"#999"}}>{[c.city,c.state].filter(Boolean).join(", ")}{c.domain&&` · ${c.domain}`}</span>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            {selectedIds.size>0&&(
              <div className="field-group">
                <label className="field-label">Agreement Title</label>
                <input type="text" className="input"
                  placeholder={defaultAgreementTitle||"Agreement"}
                  value={agreementTitle}
                  onChange={e=>{setTitleTouched(true);setAgreementTitle(e.target.value);}}/>
                <div style={{fontSize:11,color:"#999",marginTop:4,lineHeight:1.5}}>
                  {singleModule
                    ? "Shown under the customer name, in the page header, and in the file name."
                    : "Names the whole document (page header and file name). Each module keeps its own heading."}
                  {titleTouched&&defaultAgreementTitle&&agreementTitle!==defaultAgreementTitle&&(
                    <> · <button type="button"
                      onClick={()=>{setTitleTouched(false);setAgreementTitle(defaultAgreementTitle);}}
                      style={{background:"none",border:"none",padding:0,color:"#1a3a6e",cursor:"pointer",fontSize:11,textDecoration:"underline"}}>
                      reset to template name
                    </button></>
                  )}
                </div>
              </div>
            )}
            {selectedIds.size===0 ? <p className="info-empty-hint">Select modules to enter details</p> : (
              allFields.map(f=>(
                <div key={f.key} className="field-group">
                  <label className="field-label">{f.label}{f.required&&<span className="required">*</span>}</label>
                  {f.type==="measure_rows"?<MeasureRowsField field={f} value={fields[f.key]} onChange={v=>handleFieldChange(f.key,v)}/>
                  :f.type==="textarea"?<textarea className="input textarea" rows={2} placeholder={f.placeholder||""} value={fields[f.key]||""} onChange={e=>handleFieldChange(f.key,e.target.value)}/>
                  :f.type==="date"?<input type="date" className="input" value={fields[f.key]||""} onChange={e=>handleFieldChange(f.key,e.target.value)}/>
                  :<input type="text" className="input" placeholder={f.placeholder||f.default||""} value={fields[f.key]||""} onChange={e=>handleFieldChange(f.key,e.target.value)}/>}
                </div>
              ))
            )}
            {hasAddons&&(
              <div style={{marginTop:16}}>
                <div className="info-section-header">Include In This Agreement</div>
                <p className="info-empty-hint" style={{marginBottom:8}}>
                  Optional items from the templates. Unchecking one leaves it out of <em>this</em> quote —
                  the template keeps it. Use “Edit table” to change a template.
                </p>
                {Object.entries(addonGroups).map(([label,addons])=>{
                  if(!addons.length) return null;
                  return(<div key={label} style={{marginBottom:12}}>
                    <div className="addon-group-label">{label}</div>
                    {addons.map(addon=>{const enabled=!disabledAddons.has(addon.id);return(
                      <div key={addon.id} className={`addon-row${enabled?" addon-row--on":""}`} onClick={()=>toggleAddon(addon.id)}>
                        <div className={`addon-check${enabled?" addon-check--on":""}`}>{enabled&&<IconCheck/>}</div>
                        <div className="addon-info"><span className="addon-name">{addon.module}</span>{addon.price&&<span className="addon-price">{addon.price}</span>}</div>
                      </div>
                    );})}
                  </div>);
                })}
              </div>
            )}
            {customSections.length>0&&(
              <div style={{marginTop:16}}>
                <div className="info-section-header">Inserted Sections</div>
                {customSections.map((s,i)=>(
                  <div key={i} className="custom-section-item">
                    <div className="custom-section-info">
                      <span className="custom-section-type">{SECTION_TYPE_LABELS[s.type]||s.type}</span>
                      <span className="custom-section-preview">{truncate(s.content,40)}</span>
                    </div>
                    <div className="custom-section-actions">
                      <button className="icon-btn" onClick={()=>moveCustomSection(i,-1)} disabled={i===0}><IconArrowUp/></button>
                      <button className="icon-btn" onClick={()=>moveCustomSection(i,1)} disabled={i===customSections.length-1}><IconArrowDown/></button>
                      <button className="icon-btn icon-btn-danger" onClick={()=>removeCustomSection(i)}><IconX/></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="info-actions">
            {selectedIds.size>0&&<div className="selected-badge">{selectedIds.size} module{selectedIds.size!==1?"s":""} selected</div>}
            {selectedIds.size>0&&!canGenerate&&<p className="missing-hint">Enter customer name to generate</p>}
            {!reviewMode&&(
              <>
                <button onClick={handleSaveDraft} disabled={savingDraft||!selectedIds.size}
                  style={{marginTop:8,background:"#fff",color:"#1a3a6e",border:"1px solid #1a3a6e",borderRadius:8,padding:"10px 0",fontSize:13,fontWeight:500,cursor:"pointer",width:"100%",opacity:(!selectedIds.size||savingDraft)?0.5:1}}>
                  {savingDraft?"Saving…":savedDraftToken?"✓ Save Draft":"Save Draft"}
                </button>
                <button onClick={handleSubmitForApproval} disabled={!canGenerate||submitMode||submitDone}
                  style={{marginTop:8,background:submitDone?"#1D9E75":"#1a3a6e",color:"#fff",border:"none",borderRadius:8,padding:"10px 0",fontSize:14,fontWeight:600,cursor:"pointer",width:"100%",opacity:!canGenerate?0.5:1}}>
                  {submitDone?"✓ Submitted!":submitMode?"Submitting…":"Submit for Approval →"}
                </button>
                {loadedDraftClientToken&&savedDraftToken&&(
                  <button onClick={()=>window.location.href=`/send/${savedDraftToken}`}
                    style={{marginTop:6,background:"#fff",color:"#1a6e3c",border:"1.5px solid #1a6e3c",borderRadius:8,padding:"9px 0",fontSize:13,fontWeight:600,cursor:"pointer",width:"100%"}}>
                    Resend to Client (skip approval) →
                  </button>
                )}
              </>
            )}
            {reviewMode&&!reviewApproved&&!reviewAmended&&(
              <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
                <button onClick={handleApprove} style={{background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"10px 0",fontWeight:600,cursor:"pointer",fontSize:14}}>✓ Approve Agreement</button>
                <button onClick={()=>setReviewAmendOpen(true)} style={{background:"#fff",color:"#e07b3f",border:"1.5px solid #e07b3f",borderRadius:8,padding:"10px 0",fontWeight:600,cursor:"pointer",fontSize:14}}>✎ Suggest Amendments</button>
              </div>
            )}
            {selectedIds.size>0&&(
              <div style={{display:"flex",gap:6,marginTop:10}}>
                <button onClick={()=>handleExport("pdf")} disabled={!!exporting}
                  title="Download as an unsigned PDF — same formatting the client sees"
                  style={{flex:1,background:"#fff",color:"#4a5568",border:"1px solid #cbd5e0",borderRadius:8,padding:"9px 0",fontSize:13,fontWeight:500,cursor:"pointer",opacity:exporting?0.5:1}}>
                  {exporting==="pdf"?"Generating…":"⬇ PDF"}
                </button>
                <button onClick={()=>handleExport("docx")} disabled={!!exporting}
                  title="Download as an editable Word file — for hand edits before sending"
                  style={{flex:1,background:"#fff",color:"#4a5568",border:"1px solid #cbd5e0",borderRadius:8,padding:"9px 0",fontSize:13,fontWeight:500,cursor:"pointer",opacity:exporting?0.5:1}}>
                  {exporting==="docx"?"Generating…":"⬇ Word"}
                </button>
              </div>
            )}
            {selectedIds.size>0&&googleState?.configured&&(
              <button onClick={handleExportGoogleDoc} disabled={!!exporting}
                title={googleState.authed
                  ? "Create this agreement as a Google Doc in the connected Drive"
                  : "Connect Google Drive first — opens Google's sign-in"}
                style={{width:"100%",marginTop:6,background:"#fff",color:"#4a5568",border:"1px solid #cbd5e0",borderRadius:8,padding:"9px 0",fontSize:13,fontWeight:500,cursor:"pointer",opacity:exporting?0.5:1}}>
                {exporting==="gdocs"?"Creating…":googleState.authed?"📄 Open in Google Docs":"🔗 Connect Google Drive"}
              </button>
            )}
            {reviewApproved&&<div style={{color:"#1D9E75",fontWeight:600,textAlign:"center",marginTop:8}}>✓ Approved</div>}
            {reviewAmended &&<div style={{color:"#e07b3f",fontWeight:600,textAlign:"center",marginTop:8}}>Amendments submitted — team notified</div>}
            {reviewVersionMismatch&&!reviewApproved&&!reviewAmended&&(
              <div style={{marginTop:10,background:"#fff8f3",border:"1px solid #e07b3f",borderRadius:7,padding:"9px 12px",fontSize:12,color:"#b85c00",lineHeight:1.5}}>
                ⚠ This agreement was updated after your review email was sent. You are viewing the latest version.
              </div>
            )}
          </div>
        </aside>

        <div className="resize-handle" onMouseDown={startResize('info', infoPanelWidth)}/>

        {/* Center: Module Selector */}
        <div className="module-panel" style={{width: modulePanelWidth}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
            <div className="module-panel-heading">Agreement Library</div>
            <button type="button" title="Create a new agreement template"
              onClick={()=>setNewTemplateOpen(true)}
              style={{background:"#eef2ff",border:"1px solid #c7d2fe",borderRadius:5,color:"#1a3a6e",cursor:"pointer",fontSize:11,fontWeight:600,padding:"4px 10px",whiteSpace:"nowrap"}}>
              + New template
            </button>
          </div>
          {assistEnabled && (
            <button type="button" title="Paste call notes and get a proposed agreement to review"
              onClick={()=>setNotesOpen(true)}
              style={{width:"100%",marginTop:8,background:"#1a3a6e",border:"none",borderRadius:6,color:"#fff",
                      cursor:"pointer",fontSize:12,fontWeight:600,padding:"8px 10px"}}>
              ✨ Draft from notes
            </button>
          )}
          <p className="module-panel-hint">Click modules to add them to your document</p>
          {loadingProducts?<div className="loading-text">Loading modules…</div>:(
            CATEGORY_ORDER.map(catId=>{
              const catProducts=grouped[catId]||[];
              if(!catProducts.length) return null;
              return(
                <div key={catId} className="module-group">
                  <div className="module-group-label">{CATEGORY_LABELS[catId]}</div>
                  {catProducts.map(p=>{
                    const selected=selectedIds.has(p.id);
                    return(
                      <div key={p.id} className={`module-card${selected?" module-card--selected":""}`} onClick={()=>toggleProduct(p.id)} role="checkbox" aria-checked={selected}>
                        <div className={`module-check${selected?" module-check--on":""}`}>{selected&&<IconCheck/>}</div>
                        <div className="module-card-body">
                        {p.createdByUser&&<button className="module-card-delete" title="Remove from library" onClick={e=>{e.stopPropagation();handleDeleteProduct(p);}} aria-label="Delete module">×</button>}
                          <div className="module-card-name">{p.name}</div>
                          <div className="module-card-desc">{p.description}</div>
                          {p.hostingAvailable&&selected&&(
                            <label className="hosting-toggle" onClick={e=>e.stopPropagation()}>
                              <input type="checkbox" checked={hostedProducts.has(p.id)} onChange={()=>{
                                setHostedProducts(prev=>{const n=new Set(prev);const wasOn=n.has(p.id);wasOn?n.delete(p.id):n.add(p.id);
                                  if(!wasOn){setLibSourceFilter(p.name);setLibSectionFilter("Hosting");setLibTab("library");}
                                  return n;
                                });
                              }}/>
                              <span className="hosting-toggle-label">Hosted by DHIT</span>
                            </label>
                          )}
                          {p.udsAvailable&&selected&&(
                            <label className="hosting-toggle" onClick={e=>e.stopPropagation()}>
                              <input type="checkbox" checked={udsProducts.has(p.id)} onChange={()=>{
                                setUdsProducts(prev=>{const n=new Set(prev);const wasOn=n.has(p.id);wasOn?n.delete(p.id):n.add(p.id);
                                  if(!wasOn){setLibSourceFilter(p.name);setLibSectionFilter("all");setLibTab("library");}
                                  return n;
                                });
                              }}/>
                              <span className="hosting-toggle-label">Add UDS Reporting</span>
                            </label>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div className="resize-handle" onMouseDown={startResize('module', modulePanelWidth)}/>

        {/* Preview */}
        <section className="preview-panel">
          <div className="preview-toolbar">
            <span className="preview-toolbar-label">{composedSections.length>0?`Document Preview — ${selectedIds.size} module${selectedIds.size!==1?"s":""}`:("Document Preview")}</span>
            {fields.CUSTOMER_NAME&&selectedIds.size>0&&<span className="preview-toolbar-customer">{fields.CUSTOMER_NAME}</span>}
          </div>
          {hasEdits&&(
            <AgreementEditsBar count={agreementEditCount} />
          )}
          {removedFromTemplate&&(
            <div className="edit-prompt-bar">
              <span className="edit-prompt-label">
                Removed from this agreement. Also remove it from the <strong>{removedFromTemplate.productName}</strong> template?
              </span>
              <button className="btn-sm btn-sm-accent" onClick={handleRemoveFromTemplate}>Remove from Template</button>
              <button className="btn-sm btn-sm-ghost" onClick={()=>setRemovedFromTemplate(null)}>Just This Agreement</button>
            </div>
          )}
          {editPrompt&&(
            <EditPromptBar
              prompt={editPrompt}
              onSaveAsSnippet={handleSaveEditAsSnippet}
              onUpdateTemplate={handleUpdateTemplateSection}
              onUpdateSnippet={handleUpdateCurrentSnippet}
              onAddToTemplate={handleAddToTemplate}
              addTargets={addToTemplateTargets}
              onDismiss={dismissEditPrompt}
            />
          )}
          <div className="preview-scroll">
            {composedSections.length===0?(
              <div className="preview-empty"><IconEmpty/><p>Select modules on the left to build your agreement</p></div>
            ):(
              <div className="preview-doc-wrapper">
                <div className="document-page">
                  {renderPreview(composedSections,templateData,handlePriceChange,sidebarVisible?handleSectionClickFilter:undefined,{editKey:previewEditKey,editContent:previewEditContent,onStart:startPreviewEdit,onChange:setPreviewEditContent,onSave:savePreviewEdit},removeSection,handleEditTable,handleInsertAfter)}
                  <div className="prev-footer prev-footer-contact">
                    <div className="prev-footer-divider"/>
                    <div className="prev-footer-center-line">320C Monticello Avenue, New Orleans, LA 70121 &nbsp;∙&nbsp; E-mail: info@DynamicHealthIT.com</div>
                    <div className="prev-footer-center-line">Phone: (504) 309-9103 &nbsp;∙&nbsp; <span style={{textDecoration:"underline"}}>www.DynamicHealthIT.com</span></div>
                    <div className="prev-footer-center-line">Confidential and Proprietary – Quote is Valid for 30 days after date shown</div>
                  </div>
                </div>
                {composedSections.length>5&&(
                  <div className="prev-page2-header">
                    <div className="prev-page2-header-top"><span className="prev-page2-company">Dynamic Health IT, Inc.</span><span className="prev-page2-page">Page 2</span></div>
                    <div className="prev-page2-divider"/>
                    <div className="prev-page2-info"><span>{templateData.CUSTOMER_NAME||""}</span><span>{products.filter(p=>selectedIds.has(p.id)).map(p=>p.name).join(" / ")||"Agreement"}</span><span>{shortDate(templateData.DATE)}</span></div>
                    <div style={{fontSize:"8px",color:"#aaa",textAlign:"center",marginTop:4}}>— Page 2+ header shown in downloaded document —</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Snippet Sidebar — Luke: visible for all modules */}
        {sidebarVisible&&(
          <>
          <div className="resize-handle" onMouseDown={startResize('sidebar', sidebarPanelWidth)}/>
          <aside className="snippet-sidebar" style={{width: sidebarPanelWidth}}>
            <div className="snippet-sidebar-header">
              <span className="snippet-sidebar-title">Section Library</span>
              <button className="icon-btn icon-btn-accent" onClick={()=>setEditSnippet({name:"",type:"paragraph",content:"",category:"Custom"})}><IconPlus/></button>
            </div>
            <div className="snippet-tabs">
              <button className={`snippet-tab${libTab==="library"?" snippet-tab--active":""}`} onClick={()=>setLibTab("library")}>Templates</button>
              <button className={`snippet-tab${libTab==="snippets"?" snippet-tab--active":""}`} onClick={()=>setLibTab("snippets")}>My Snippets{snippets.length>0&&` (${snippets.length})`}</button>
            </div>
            <div className="snippet-search-row">
              <div className="snippet-search-wrap">
                <IconSearch/>
                <input type="text" className="snippet-search" placeholder="Search sections…" value={libSearch} onChange={e=>setLibSearch(e.target.value)}/>
                {libSearch&&<button className="snippet-search-clear" onClick={()=>setLibSearch("")}><IconX/></button>}
              </div>
              {libTab==="library"&&(
                <>
                  <select className="snippet-source-filter" value={libSourceFilter} onChange={e=>setLibSourceFilter(e.target.value)}>
                    {libSources.map(s=><option key={s} value={s}>{s==="all"?"All Templates":s}</option>)}
                  </select>
                  <select className="snippet-source-filter" value={libSectionFilter} onChange={e=>setLibSectionFilter(e.target.value)} style={{marginTop:4}}>
                    <option value="all">All Sections</option>
                    {SECTION_FILTER_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </>
              )}
            </div>

            {/* Standard snippet editor */}
            {editSnippet&&(
              <div className="snippet-editor">
                <div className="snippet-editor-title">{editSnippet.id?"Edit Snippet":"New Snippet"}</div>
                <input ref={editNameRef} className="input snippet-editor-input" placeholder="Snippet name" value={editSnippet.name} onChange={e=>setEditSnippet(prev=>({...prev,name:e.target.value}))}/>
                <div className="snippet-editor-row">
                  <select className="input snippet-editor-select" value={SNIPPET_TYPE_LABELS[editSnippet.type]?editSnippet.type:"paragraph"} onChange={e=>setEditSnippet(prev=>({...prev,type:e.target.value}))}>
                    {Object.entries(SNIPPET_TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                  <input className="input snippet-editor-input" placeholder="Category" value={editSnippet.category} onChange={e=>setEditSnippet(prev=>({...prev,category:e.target.value}))}/>
                </div>
                <textarea className="input textarea snippet-editor-textarea" rows={4} placeholder="Section content… (supports {{CUSTOMER_NAME}} variables)" value={editSnippet.content} onChange={e=>setEditSnippet(prev=>({...prev,content:e.target.value}))}/>
                <div className="snippet-editor-actions">
                  <button className="btn-sm btn-sm-accent" onClick={handleSaveSnippet}><IconSave/> Save</button>
                  <button className="btn-sm btn-sm-ghost" onClick={()=>setEditSnippet(null)}>Cancel</button>
                </div>
              </div>
            )}

            <div className="snippet-list">
              {libTab==="library"?(
                filteredLibrary.length===0?<p className="snippet-empty">No matching sections found</p>:(
                  filteredLibrary.map(item=>(
                    <div key={item.id} data-libid={item.id} className={`snippet-card${libHighlightId===item.id?" snippet-card--highlight":""}`}>
                      <div className="snippet-card-top">
                        <span className="snippet-card-type">{SECTION_TYPE_LABELS[item.type]||item.type}</span>
                        {item.subSource&&<span className={`snippet-card-badge snippet-card-badge--${item.subSource}`}>{item.subSource.toUpperCase()}</span>}
                        <span className="snippet-card-source">{item.source}</span>
                      </div>
                      {item.section&&item.section!=="Other"&&<span className="snippet-card-category">{item.section}</span>}
                      <div className="snippet-card-content">{truncate(item.content,120)}</div>
                      <div className="snippet-card-actions">
                        <button className="btn-sm btn-sm-accent" onClick={()=>insertLibraryItem(item)}><IconPlus/> Insert</button>
                      </div>
                    </div>
                  ))
                )
              ):(
                filteredSnippets.length===0?(
                  <div className="snippet-empty"><p style={{fontWeight:600,marginBottom:4}}>No custom snippets yet</p><p style={{fontSize:12,color:"#888",margin:"0 0 12px",lineHeight:1.5}}>Save your own reusable clauses here — special terms, custom paragraphs, extra notes — to drop into any agreement. Standard product language lives in the Templates tab.</p><button className="btn-sm btn-sm-accent" onClick={()=>setEditSnippet({name:"",type:"paragraph",content:"",category:"Custom"})}><IconPlus/> Create your first snippet</button></div>
                ):(
                  filteredSnippets.map(snip=>(
                    <div key={snip.id} className="snippet-card">
                      <div className="snippet-card-top"><span className="snippet-card-name">{snip.name}</span><span className="snippet-card-type">{SECTION_TYPE_LABELS[snip.type]||snip.type}</span></div>
                      {snip.category&&<span className="snippet-card-category">{snip.category}</span>}
                      <div className="snippet-card-content">{truncate(snip.content,120)}</div>
                      <div className="snippet-card-actions">
                        <button className="btn-sm btn-sm-accent" onClick={()=>insertSnippet(snip)}><IconPlus/> Insert</button>
                        <button className="btn-sm btn-sm-ghost" onClick={()=>setEditSnippet({...snip})} title="Edit snippet in library"><IconEdit/></button>
                        <button className="btn-sm btn-sm-danger" onClick={()=>handleDeleteSnippet(snip.id)}><IconTrash/></button>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>
          </aside>
          </>
        )}
      </div>

      {newTemplateOpen && (
        <NewTemplateModal
          products={products}
          onClose={()=>setNewTemplateOpen(false)}
          onCreated={handleTemplateCreated}
        />
      )}

      {notesOpen && (
        <DraftFromNotes
          products={products}
          onApply={applyNotesDraft}
          onClose={()=>setNotesOpen(false)}
        />
      )}

      {tableEditor && (
        <PricingRowsEditor
          productId={tableEditor.productId}
          productName={tableEditor.productName}
          sectionIdx={tableEditor.sectionIdx}
          sub={tableEditor.sub}
          dualYear={tableEditor.dualYear}
          yearLabels={tableEditor.yearLabels}
          label={tableEditor.label}
          rows={tableEditor.rows}
          agreementRows={tableEditor.agreementRows}
          dynamic={tableEditor.dynamic}
          hasOverride={!!tableOverrides[tableEditor.srcKey]}
          onClose={()=>setTableEditor(null)}
          onSaved={handleTableSaved}
          onApplyToAgreement={handleTableAppliedToAgreement}
          onResetAgreement={handleTableOverrideReset}
        />
      )}

      {toast&&(
        <div className={`toast toast-${toast.type}`}>
          {toast.type==="success"?<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>:<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>}
          {toast.msg}
        </div>
      )}

      {/* ── Suggest Amendments modal ─────────────────────────────────────────── */}
      {reviewAmendOpen&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:3000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={()=>!amendSubmitting&&setReviewAmendOpen(false)}>
          <div style={{background:"#fff",borderRadius:12,padding:"28px 28px 24px",width:500,maxWidth:"95vw",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:17,fontWeight:700,color:"#1a3a6e",marginBottom:6}}>Suggest Amendments</div>
            <p style={{fontSize:13,color:"#666",marginBottom:16,lineHeight:1.6}}>
              Describe the changes needed below. If you edited any text directly in the agreement preview, those changes will be included automatically.
              Both reviewers will be re-emailed with the updated agreement.
            </p>
            <textarea
              placeholder="e.g. Update pricing on page 2, clarify the termination clause in section 4…"
              value={amendNote}
              onChange={e=>setAmendNote(e.target.value)}
              rows={5}
              style={{width:"100%",padding:"10px 12px",border:"1.5px solid #e07b3f",borderRadius:8,fontSize:13,resize:"vertical",boxSizing:"border-box",outline:"none",lineHeight:1.6}}
              autoFocus
            />
            <div style={{display:"flex",gap:10,marginTop:16,justifyContent:"flex-end"}}>
              <button onClick={()=>setReviewAmendOpen(false)} disabled={amendSubmitting}
                style={{padding:"9px 18px",borderRadius:7,border:"1px solid #ddd",background:"#fff",color:"#555",fontSize:13,fontWeight:500,cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={handleAmend} disabled={amendSubmitting||!amendNote.trim()}
                style={{padding:"9px 22px",borderRadius:7,border:"none",background:(!amendNote.trim()||amendSubmitting)?"#f5c9a8":"#e07b3f",color:"#fff",fontSize:13,fontWeight:600,cursor:(!amendNote.trim()||amendSubmitting)?"default":"pointer"}}>
                {amendSubmitting?"Submitting…":"Submit Amendments →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
